/* Rossi Konsultantė — search-bar AI assistant.
   Additive: predictive search and /search stay byte-identical for anyone who
   never asks a question. Pure logic lives on window.RossiKonsultante (testable);
   DOM wiring happens in init() at the bottom. No dependencies. */
(function () {
  'use strict';

  var RK = {};

  // ---------------- router: three lanes, no visible fork (§4) ----------------

  RK.fold = function (s) {
    return String(s || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '');
  };

  // LT / LV / ET / EN / DE signals
  RK.INTERROGATIVES = ('koks kokia kokie kokios kaip kada kodel kur ar kas kiek kuris kuri kam ' +
    'kads kada kadi kapec vai cik kurs kura kadel ' +
    'mis kuidas millal miks kus kumb milline kas ' +
    'what which how when why where who does is can should do are ' +
    'welche welcher wie was wann warum wo ist kann').split(' ');

  RK.IMPERATIVE_STEMS = ('rekomenduo parink pasiulyk pasakyk padek isrink surask palygin patark papasakok sudek sudaryk ' +
    'iesak ieteic izvel atrod salidzin palidz pastast ' +
    'soovita leia vordle aita vali koosta ' +
    'recommend suggest find compare help pick choose build tell explain ' +
    'empfehl finde vergleich hilf zeig erklar').split(' ');

  RK.FIRST_PERSON = ('man mano manai mums mus mans manam mulle mu minu my me i mir mein meine').split(' ');

  RK.hasQuestionSignal = function (query) {
    if (query.indexOf('?') !== -1) return true;
    var qf = RK.fold(query);
    var words = qf.split(/\s+/).filter(Boolean);
    if (!words.length) return false;
    for (var i = 0; i < words.length; i++) {
      var w = words[i].replace(/[^a-z0-9]/g, '');
      if (RK.INTERROGATIVES.indexOf(w) !== -1) return true;
      if (RK.FIRST_PERSON.indexOf(w) !== -1) return true;
      for (var j = 0; j < RK.IMPERATIVE_STEMS.length; j++) {
        if (w.length >= RK.IMPERATIVE_STEMS[j].length && w.indexOf(RK.IMPERATIVE_STEMS[j]) === 0) return true;
      }
    }
    return false;
  };

  RK.matchesLocalIndex = function (query, index) {
    if (!index) return false;
    var qf = RK.fold(query).trim();
    if (qf.length < 2) return false;
    var i;
    var brands = index.brands || [];
    for (i = 0; i < brands.length; i++) {
      if (brands[i].indexOf(qf) === 0 || qf.indexOf(brands[i]) !== -1) return true;
    }
    var cols = index.collections || [];
    for (i = 0; i < cols.length; i++) {
      if (cols[i].indexOf(qf) === 0) return true;
    }
    if (qf.length >= 4) {
      var titles = index.titles || [];
      for (i = 0; i < titles.length; i++) {
        if (titles[i].indexOf(qf) !== -1) return true;
      }
    }
    return false;
  };

  // A pasted URL to one of our six storefronts is a product-detail question,
  // not a predictive-search query. The server independently validates it and
  // resolves live facts; this client check only chooses the correct lane.
  // Keep the host list in step with MARKETS in server/config.js.
  RK.trustedProductHandle = function (query) {
    var m = String(query || '').trim().match(/^https:\/\/(?:www\.)?(?:rossi\.lt|rossi\.lv|rossiskincare\.ee|rossiskincare\.com|rossiskincare\.de|rossiskincare\.fi)\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?products\/([a-z0-9][a-z0-9_-]{0,254})\/?(?:[?#].*)?$/i);
    return m ? m[1].toLowerCase() : '';
  };

  // Strong ask signals outrank a brand/title hit: a question mark, a LEADING
  // interrogative or imperative, or first-person wording in a longer phrase
  // means the customer is asking, not looking something up ("anua serumas
  // mano odai", "surask serumą sausai odai"). The assistant can always hand
  // back to search via its <search> escape, so over-catching is cheap.
  RK.hasStrongAskSignal = function (query) {
    var q = String(query || '');
    if (q.indexOf('?') !== -1) return true;
    var words = RK.fold(q)
      .split(/\s+/)
      .map(function (w) { return w.replace(/[^a-z0-9]/g, ''); })
      .filter(Boolean);
    if (!words.length) return false;
    if (words.length >= 2) {
      if (RK.INTERROGATIVES.indexOf(words[0]) !== -1) return true;
      for (var j = 0; j < RK.IMPERATIVE_STEMS.length; j++) {
        if (words[0].length >= RK.IMPERATIVE_STEMS[j].length && words[0].indexOf(RK.IMPERATIVE_STEMS[j]) === 0) return true;
      }
    }
    if (words.length >= 3) {
      for (var i = 0; i < words.length; i++) {
        if (RK.FIRST_PERSON.indexOf(words[i]) !== -1) return true;
      }
    }
    return false;
  };

  // -> 'search' | 'question' | 'ambiguous'
  RK.detectLane = function (query, index) {
    var q = String(query || '').trim();
    if (!q) return 'search';
    if (RK.trustedProductHandle(q)) return 'question';
    if (RK.hasStrongAskSignal(q)) return 'question';
    var signal = RK.hasQuestionSignal(q);
    var product = RK.matchesLocalIndex(q, index);
    if (signal && product) return 'ambiguous';
    if (signal) return 'question';
    var words = q.split(/\s+/).filter(Boolean);
    if (!product && words.length >= 4) return 'ambiguous';
    return 'search';
  };

  // Nudge trigger (pure, testable): mobile fires on PDP views OR active time,
  // desktop on active time only. -> 'pdp2' | 'timer' | null
  RK.nudgeTrigger = function (mobile, activeSeconds, pdpViews, nc) {
    if (!nc) return null;
    if (mobile) {
      if (pdpViews >= (nc.pdp_views_mobile || 2)) return 'pdp2';
      if (activeSeconds >= (nc.active_seconds_mobile || 60)) return 'timer';
      return null;
    }
    return activeSeconds >= (nc.active_seconds_desktop || 90) ? 'timer' : null;
  };

  // ------------------------- tiny utils -------------------------

  RK.hashBucket = function (str) {
    var h = 5381;
    for (var i = 0; i < str.length; i++) h = ((h << 5) + h + str.charCodeAt(i)) >>> 0;
    return h % 100;
  };

  function esc(s) {
    return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  RK._esc = esc;

  // Parse SSE frames from a fetch stream buffer. Returns [frames, rest].
  RK.parseSSE = function (buffer) {
    var frames = [];
    var parts = buffer.split('\n\n');
    var rest = parts.pop();
    for (var i = 0; i < parts.length; i++) {
      var lines = parts[i].split('\n');
      var ev = 'message';
      var data = '';
      for (var j = 0; j < lines.length; j++) {
        if (lines[j].indexOf('event:') === 0) ev = lines[j].slice(6).trim();
        else if (lines[j].indexOf('data:') === 0) data += lines[j].slice(5).trim();
      }
      if (data) {
        try { frames.push({ event: ev, data: JSON.parse(data) }); } catch (e) { /* skip */ }
      }
    }
    return [frames, rest];
  };

  // Parse a stored assistant message (text with inline tag markup) back into
  // renderable pieces — used to restore a conversation after navigation (B1).
  // <search> is intentionally ignored on restore (never re-navigate).
  RK.parseStored = function (content) {
    var pieces = [];
    var re = /<products>([\s\S]*?)<\/products>|<compare>([\s\S]*?)<\/compare>|<routine>([\s\S]*?)<\/routine>|<chips>([\s\S]*?)<\/chips>|<cta\b([^>]*)>(?:\s*<\/cta>)?|<search>[\s\S]*?<\/search>|<profile\b[^>]*>(?:\s*<\/profile>)?/gi;
    var last = 0;
    var m;
    function handles(s) {
      return s.split(',').map(function (x) { return x.trim().toLowerCase(); }).filter(Boolean).slice(0, 6);
    }
    while ((m = re.exec(content))) {
      if (m.index > last) pieces.push({ type: 'text', text: content.slice(last, m.index) });
      if (m[1] != null) pieces.push({ type: 'tag', tag: 'products', value: handles(m[1]) });
      else if (m[2] != null) pieces.push({ type: 'tag', tag: 'compare', value: handles(m[2]) });
      else if (m[3] != null) {
        // items are "handle" or "step:handle" (captioned routine cards);
        // labels ride in parallel arrays, bare handles keep empty labels
        var value = { am: [], pm: [], amLabels: [], pmLabels: [] };
        m[3].split('|').forEach(function (part) {
          var mm = part.trim().match(/^(am|pm)\s*:\s*(.*)$/i);
          if (!mm) return;
          var key = mm[1].toLowerCase();
          mm[2].split(',').forEach(function (raw) {
            if (value[key].length >= 6) return;
            var item = raw.trim();
            if (!item) return;
            var label = '';
            var ci = item.indexOf(':');
            if (ci > 0) { label = item.slice(0, ci).trim(); item = item.slice(ci + 1).trim(); }
            if (!item) return;
            value[key].push(item.toLowerCase());
            value[key + 'Labels'].push(label.slice(0, 24));
          });
        });
        pieces.push({ type: 'tag', tag: 'routine', value: value });
      } else if (m[4] != null) {
        // <chips> restore was missing (the tag postdates this parser) — the
        // raw tag leaked into restored transcripts as literal text
        var chipLabels = m[4].split(',').map(function (x) { return x.trim().slice(0, 24); }).filter(Boolean).slice(0, 6);
        if (chipLabels.length) pieces.push({ type: 'tag', tag: 'chips', value: chipLabels });
      } else if (m[5] != null) {
        var attrs = {};
        var ar = /([a-zA-Z_-]+)\s*=\s*"([^"]*)"/g;
        var am;
        while ((am = ar.exec(m[5]))) attrs[am[1].toLowerCase()] = am[2];
        if (attrs.type === 'quiz' || attrs.type === 'bis' || attrs.type === 'contact') pieces.push({ type: 'tag', tag: 'cta', value: attrs });
      }
      last = re.lastIndex;
    }
    if (last < content.length) pieces.push({ type: 'text', text: content.slice(last) });
    return pieces;
  };

  // In-chat skin quiz scoring — exact port of the page quiz (quiz_version v2,
  // skin-quiz-chat-rebuild brief §2). Answer keys are the shared analytics
  // contract with the page quiz: keep them byte-identical. Q4 concerns and
  // Q5 sensitivity never score; ties resolve to the Q1 type; all-zero → misri.
  var QUIZ_POINTS = {
    q1_dry: { sausa: 2 }, q1_mixed: { misri: 2 }, q1_oily: { riebi: 2 }, q1_normal: { normali: 2 },
    q2_dry_or_normal: { sausa: 1, normali: 1 }, q2_mixed: { misri: 1 }, q2_oily: { riebi: 1 },
    q3_dry: { sausa: 1 }, q3_normal: { normali: 1 }, q3_none: { riebi: 1, normali: 1 },
  };
  var QUIZ_Q1_TYPE = { q1_dry: 'sausa', q1_mixed: 'misri', q1_oily: 'riebi', q1_normal: 'normali' };
  RK.quizEvaluate = function (answerKeys) {
    var scores = { riebi: 0, misri: 0, sausa: 0, normali: 0 };
    var q1Type = null;
    (answerKeys || []).forEach(function (k) {
      var p = QUIZ_POINTS[k];
      if (p) for (var t in p) scores[t] += p[t];
      if (QUIZ_Q1_TYPE[k]) q1Type = QUIZ_Q1_TYPE[k];
    });
    var best = 'misri';
    var bestScore = -1;
    var tie = false;
    ['riebi', 'misri', 'sausa', 'normali'].forEach(function (t) {
      if (scores[t] > bestScore) { best = t; bestScore = scores[t]; tie = false; }
      else if (scores[t] === bestScore) tie = true;
    });
    if (bestScore <= 0) return 'misri';
    if (tie) return q1Type || 'misri';
    return best;
  };

  if (typeof window !== 'undefined') window.RossiKonsultante = RK;
  if (typeof window === 'undefined' || window.__RK_NO_AUTOINIT) return;

  // ============================ DOM app ============================

  var cfgEl = document.getElementById('rk-config');
  if (!cfgEl) return;
  var cfg;
  try { cfg = JSON.parse(cfgEl.textContent); } catch (e) { return; }
  var S = cfg.strings || {};
  var ROOT = cfg.root || '/';
  var API = ROOT.replace(/\/$/, '') + '/apps/konsultante';

  var state = {
    remote: null,
    enabled: false,
    index: null,
    indexLoading: false,
    session: null,
    history: [],
    sending: false,
    turnstileToken: null,
    turnstileNeeded: false,
    pendingSend: null,
    opened: false,
    openedBeaconSent: false,
    openedVia: null, // entry source when the panel opened empty (nudge/nav/…)
  };

  // ---------------- session ----------------
  // Conversations linger (2026-07-11): the session id AND the transcript live
  // in localStorage for 7 days after the last chat MESSAGE — closing the tab
  // no longer kills the chat, "Tęsti pokalbį" works across tabs and days.
  // Freshness is tied to chat activity (saveHistory), not page loads, so a
  // browsing-only visitor doesn't drag an old chat around forever. Cost note:
  // the server already caps model context (history_turns) and messages per
  // session — resume adds no cost exposure. Profile (rk:profile) was always
  // durable; this closes the asymmetry.
  var SESSION_TTL_MS = 7 * 24 * 60 * 60 * 1000;

  function session() {
    var now = Date.now();
    try {
      var raw = JSON.parse(localStorage.getItem('rk:sess') || 'null');
      if (raw && raw.id && now - raw.ts < SESSION_TTL_MS) {
        return raw.id;
      }
    } catch (e) { /* fresh */ }
    var id = 'rk-' + now.toString(36) + '-' + Math.random().toString(36).slice(2, 10);
    try {
      // sweep expired transcripts before adopting the new id (never touch
      // other rk:* keys — profile/nudge/seen live there)
      for (var i = localStorage.length - 1; i >= 0; i--) {
        var k = localStorage.key(i);
        if (k && k.indexOf('rk:hist:') === 0) localStorage.removeItem(k);
      }
      localStorage.setItem('rk:sess', JSON.stringify({ id: id, ts: now }));
    } catch (e) {}
    state.history = [];
    return id;
  }
  function touchSession() {
    try { localStorage.setItem('rk:sess', JSON.stringify({ id: state.session, ts: Date.now() })); } catch (e) {}
  }
  function loadHistory() {
    try {
      var h = JSON.parse(localStorage.getItem('rk:hist:' + state.session) || 'null');
      // migration: transcripts used to live in sessionStorage — adopt a
      // mid-flight one once so the rollout doesn't blank open chats
      if (!h) h = JSON.parse(sessionStorage.getItem('rk:hist:' + state.session) || '[]');
      if (Array.isArray(h)) state.history = h;
    } catch (e) { state.history = []; }
  }
  function saveHistory() {
    try {
      localStorage.setItem('rk:hist:' + state.session, JSON.stringify(state.history.slice(-20)));
      touchSession();
    } catch (e) {}
  }

  // ---------------- beacons ----------------
  function beacon(type, payload) {
    try {
      var body = JSON.stringify({ session_id: state.session, type: type, payload: payload || null });
      var url = API + '/event';
      if (navigator.sendBeacon) {
        navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
      } else {
        fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: body, keepalive: true });
      }
    } catch (e) { /* never break the shop */ }
  }

  // ---------------- panel UI ----------------
  var panel, msgsEl, inputEl, sendBtn, turnstileWrap, scrollEl;

  function buildPanel() {
    if (panel) return;
    panel = document.createElement('div');
    panel.className = 'rk-panel';
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'false');
    panel.setAttribute('aria-label', S.title || 'Rossi konsultante');
    panel.hidden = true;
    panel.innerHTML =
      '<div class="rk-panel__head"><span class="rk-panel__flower">🌸</span><span class="rk-panel__title">' + esc(S.title) + '</span>' +
      '<button type="button" class="rk-panel__close" aria-label="' + esc(S.close) + '">&times;</button></div>' +
      '<div class="rk-panel__scroll"><div class="rk-msgs"></div><div class="rk-starter"></div></div>' +
      '<div class="rk-panel__turnstile" hidden><p class="rk-panel__verify">' + esc(S.verify) + '</p><div class="rk-turnstile-mount"></div></div>' +
      '<form class="rk-panel__form"><textarea class="rk-input" rows="1" placeholder="' + esc(S.input_ph) + '" maxlength="1000"></textarea>' +
      '<button type="submit" class="rk-send" aria-label="' + esc(S.send) + '"><svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path fill="#ffffff" d="M3.4 20.4 21.85 12 3.4 3.6l-.01 6.53L15 12 3.39 13.87z"/></svg></button></form>' +
      '<div class="rk-panel__foot">' + esc(S.disclaimer) + '</div>';
    document.body.appendChild(panel);

    msgsEl = panel.querySelector('.rk-msgs');
    inputEl = panel.querySelector('.rk-input');
    sendBtn = panel.querySelector('.rk-send');
    turnstileWrap = panel.querySelector('.rk-panel__turnstile');
    scrollEl = panel.querySelector('.rk-panel__scroll');
    // follow-state: only an UPWARD move can disengage (our snaps only ever
    // move down, so an upward move is always the customer); reaching the
    // bottom by any means re-engages. Judging by gap alone is racy: content
    // that grows between a snap and its scroll event fakes a big gap.
    // A content SHRINK (every placeholder→real card swap is one) makes the
    // browser clamp scrollTop downward, which by scrollTop alone is
    // indistinguishable from a finger swipe up — so the reader was silently
    // unstuck mid-answer. Only trust a downward move when scrollHeight did
    // not shrink with it. A REAL upward move also cancels the post-open
    // forced snap, so restored card images can no longer yank a customer who
    // scrolled up to re-read back down (2026-07-25).
    var lastScrollTop = 0;
    var lastScrollH = 0;
    scrollEl.addEventListener('scroll', function () {
      var top = scrollEl.scrollTop;
      var h = scrollEl.scrollHeight;
      var gap = h - top - scrollEl.clientHeight;
      if (top < lastScrollTop - 1 && h >= lastScrollH) {
        stickBottom = gap < 48;
        settleScrollUntil = 0;
      } else if (gap < 48) stickBottom = true;
      lastScrollTop = top;
      lastScrollH = h;
    }, { passive: true });

    panel.querySelector('.rk-panel__close').addEventListener('click', closePanel);
    panel.querySelector('.rk-panel__form').addEventListener('submit', function (e) {
      e.preventDefault();
      var text = inputEl.value.trim();
      if (text) submitInputText(text);
    });
    inputEl.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        var text = inputEl.value.trim();
        if (text) submitInputText(text);
      }
    });
    inputEl.addEventListener('input', function () {
      if (state.pendingSend && inputEl.value !== state.pendingSend.text) state.pendingSend = null;
      inputEl.style.height = 'auto';
      inputEl.style.height = Math.min(inputEl.scrollHeight, 120) + 'px';
    });
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      if (state.opened) closePanel();
    });
    // clicking anywhere outside the panel closes it (desktop floating dialog;
    // harmless on mobile where the panel covers the whole screen). Elements
    // that themselves open the chat are excluded so the open click survives.
    document.addEventListener('click', function (e) {
      if (!state.opened || !panel || panel.hidden) return;
      if (panel.contains(e.target)) return;
      // clicks inside theme drawers/modals (e.g. the cart drawer opened from
      // an add-to-cart, incl. its backdrop) must not close the conversation
      if (e.target.closest && e.target.closest('.rk-continue, .rk-chip, .rk-nudge, .rk-launcher, [data-rk-rescue], [data-rk-open], [data-rk-ask], [data-rk-cart], [data-rk-cart-recs], [data-rk-empty], a[href*="#konsultante"], .drawer, .modal')) return;
      closePanel();
    });

    // starter: intro line (C3) + suggestion chips (shown while conversation is empty)
    var starter = panel.querySelector('.rk-starter');
    if (S.intro) {
      var introEl = document.createElement('div');
      introEl.className = 'rk-starter__intro';
      introEl.textContent = S.intro;
      starter.appendChild(introEl);
    }
    // suggestion chips: most-popular real customer questions (from /config,
    // aggregated server-side) first, padded with the static per-locale set
    var chipTexts = (state.remote && Array.isArray(state.remote.chips)) ? state.remote.chips.slice(0) : [];
    (S.chips || []).forEach(function (c) { chipTexts.push(c); });
    var seenChips = {};
    chipTexts.filter(function (c) {
      var k = String(c || '').trim().toLowerCase();
      if (!k || seenChips[k]) return false;
      seenChips[k] = 1;
      return true;
    }).slice(0, 4).forEach(function (chip) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'rk-chip';
      b.textContent = chip;
      b.addEventListener('click', function () { sendMessage(chip, 'chip'); });
      starter.appendChild(b);
    });
    // in-chat skin quiz entry (lt/lv/et): runs client-side, no model turn
    if (QUIZ_I18N[cfg.locale]) {
      var quizChip = document.createElement('button');
      quizChip.type = 'button';
      quizChip.className = 'rk-chip rk-chip--quiz';
      quizChip.textContent = '✦ ' + S.quiz;
      quizChip.addEventListener('click', startQuiz);
      starter.appendChild(quizChip);
    }

    wireViewportPinning();

    // delegated interactions inside messages (cards, thumbs, ctas)
    msgsEl.addEventListener('submit', function (e) {
      var form = e.target;
      if (form.getAttribute && form.getAttribute('data-type') === 'add-to-cart-form') {
        e.preventDefault();
        addToCart(form);
      }
    }, true);
    // variant select on chat cards: keep the card's price in sync with the
    // chosen variant (money strings ride on the options)
    msgsEl.addEventListener('change', function (e) {
      var sel = e.target;
      if (!sel.classList || !sel.classList.contains('rk-card__variant')) return;
      var card = sel.closest('.rk-card');
      var priceEl = card && card.querySelector('.rk-card__price');
      var opt = sel.options[sel.selectedIndex];
      if (!priceEl || !opt) return;
      var compare = opt.getAttribute('data-compare');
      var price = opt.getAttribute('data-price') || '';
      priceEl.textContent = '';
      if (compare) {
        var s = document.createElement('s');
        s.textContent = compare;
        var sale = document.createElement('span');
        sale.className = 'rk-card__price-sale';
        sale.textContent = ' ' + price;
        priceEl.appendChild(s);
        priceEl.appendChild(sale);
      } else {
        priceEl.textContent = price;
      }
    }, true);
    // right after open/restore, card images finishing may grow the log
    msgsEl.addEventListener('load', function () { settleScroll(); }, true);
    msgsEl.addEventListener('click', function (e) {
      // card media/title are plain links to the product page — chat history
      // survives navigation, so the customer can come back via "Tęsti pokalbį"
      var a = e.target.closest && e.target.closest('a[href*="/products/"]');
      if (a) beacon('click', { href: a.getAttribute('href') });
      var thumb = e.target.closest && e.target.closest('[data-rk-thumb]');
      if (thumb && !thumb.disabled) {
        beacon(thumb.getAttribute('data-rk-thumb') === 'up' ? 'thumbs_up' : 'thumbs_down');
        var row = thumb.parentElement;
        row.querySelectorAll('[data-rk-thumb]').forEach(function (b) { b.disabled = true; });
        thumb.classList.add('rk-thumb--on');
        if (S.thanks && !row.querySelector('.rk-thumbs__thanks')) {
          var ok = document.createElement('span');
          ok.className = 'rk-thumbs__thanks';
          ok.textContent = S.thanks;
          row.appendChild(ok);
        }
      }
    });
  }

  // ---------------- B1: restore a conversation after navigation ----------------
  var historyRendered = false;

  function renderHistory() {
    if (historyRendered) return;
    // arm the guard even when history is EMPTY — the live bubbles are the
    // render. Unarmed, the next openPanel (every sendMessage calls it)
    // appended the whole history again: duplicated messages after
    // close-and-return, old card blocks re-fetching mid-conversation, and
    // <chips> tags leaking as literal text.
    historyRendered = true;
    if (!state.history.length) return;
    state.history.forEach(function (msg) {
      if (msg.role === 'user') {
        attachPctx(addMsg('user', msg.content), msg.pctx_hidden ? null : msg.pctx);
        return;
      }
      var aEl = addMsg('assistant', '');
      RK.parseStored(msg.content).forEach(function (piece) {
        if (piece.type === 'text') {
          // tidy spaces but KEEP paragraph breaks — collapsing all whitespace
          // turned restored answers into one continuous blob (pre-wrap bubbles).
          // Asterisks: markdown bold the model slipped in before the server
          // started stripping it — clean older stored sessions too.
          var t = piece.text
            .replace(/\*/g, '')
            .replace(/[ \t]+/g, ' ')
            .replace(/ ?\n ?/g, '\n')
            .replace(/\n{3,}/g, '\n\n')
            .trim();
          if (t) {
            var span = document.createElement('span');
            span.textContent = t + ' ';
            aEl.appendChild(span);
          }
        } else {
          handleTag(aEl, piece.tag, piece.value); // cards re-fetched live, never cached
        }
      });
    });
    try { localStorage.setItem('rk:seen', String(state.history.length)); } catch (e) {}
    scrollDown(true);
  }

  // Collapse the theme's search UI (expanded overlay + body.search-open)
  // when the chat takes over — otherwise closing the chat reveals the stale
  // white search overlay left open underneath (its own click-outside logic
  // ignores clicks inside our panel). Uses the theme's own state routine.
  function closeThemeSearch() {
    document.querySelectorAll('predictive-search').forEach(function (ps) {
      try {
        if (typeof ps.toggleSearchState === 'function') ps.toggleSearchState(false);
        else {
          ps.classList.remove('predictive-search-open');
          document.body.classList.remove('search-open');
        }
        var input = ps.querySelector('input[type="search"]');
        if (input) input.blur();
      } catch (e) { /* theme internals shifted — cosmetic only */ }
    });
  }

  function openPanel(route) {
    buildPanel();
    renderHistory();
    // remember how an EMPTY conversation was entered — the first message's
    // route then reports it (entry_source semantics on the route column)
    if (route && !state.openedVia && !state.history.length) state.openedVia = route;
    if (nudgeEl) hideNudge();
    try { sessionStorage.setItem('rk:opened', '1'); } catch (e) {}
    if (state.opened) return;
    state.opened = true;
    panel.hidden = false;
    closeThemeSearch();
    lockBody();
    document.documentElement.classList.add('rk-open');
    positionPanel();
    renderStarterVisibility();
    updateContinueChips();
    // scroll only works once the panel is visible (hidden = zero layout);
    // keep pinning for a moment while restored card blocks stream in
    if (panel.__rkApplyViewport) panel.__rkApplyViewport();
    scrollDown(true);
    settleScrollUntil = Date.now() + 2000;
    try { localStorage.setItem('rk:seen', String(state.history.length)); } catch (e) {}
    updateLauncher();
    if (!state.openedBeaconSent) {
      state.openedBeaconSent = true;
      beacon('opened', { route: route || 'unknown' });
    }
    setTimeout(function () { inputEl.focus(); }, 60);
  }

  function closePanel() {
    if (!panel) return;
    quizAbandon(); // mid-quiz close = abandoned, no profile write
    state.opened = false;
    panel.hidden = true;
    panel.style.top = '';
    panel.style.height = '';
    unlockBody();
    document.documentElement.classList.remove('rk-open');
    document.documentElement.classList.remove('rk-cart-origin');
    updateContinueChips();
    updateLauncher();
  }

  // Phones: while the chat is open, hard-lock the page behind it. iOS
  // ignores overflow:hidden when it auto-scrolls to a focused input and
  // drags fixed elements along with the document — body position:fixed is
  // the only reliable lock. Scroll position is restored on close.
  var bodyLocked = false;
  var bodyLockY = 0;
  function lockBody() {
    if (bodyLocked || !window.matchMedia('(max-width: 767px)').matches) return;
    bodyLocked = true;
    bodyLockY = window.scrollY || window.pageYOffset || 0;
    document.body.style.position = 'fixed';
    document.body.style.top = -bodyLockY + 'px';
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
  }
  function unlockBody() {
    if (!bodyLocked) return;
    bodyLocked = false;
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    window.scrollTo(0, bodyLockY);
  }

  // iOS keyboard: it never resizes the layout viewport — it SCROLLS the
  // visual one, shoving a fixed full-height panel half off screen (customer
  // sees the page + the panel's bottom edge mid-screen). While the panel is
  // open on a phone, pin it to the visual viewport instead: full-screen chat
  // with the input row sitting right on top of the keyboard.
  function wireViewportPinning() {
    if (!window.visualViewport) return; // older browsers keep the static layout
    var vv = window.visualViewport;
    function apply() {
      if (!state.opened || !panel || panel.hidden) return;
      if (!window.matchMedia('(max-width: 767px)').matches) return;
      // While the page is ZOOMED the visual viewport is a small window onto a
      // scaled layout — pinning the panel to it would shrink the chat to that
      // window and strand the customer. Inputs are all 16px now so focus
      // cannot zoom, but a deliberate pinch still can: leave the layout alone
      // and let it resume when they zoom back out (2026-07-25).
      if (vv.scale && vv.scale > 1.02) {
        panel.style.top = '';
        panel.style.height = '';
        return;
      }
      panel.style.top = vv.offsetTop + 'px';
      panel.style.height = vv.height + 'px';
      // keyboard opening is the customer's own action — keep the latest
      // messages in view above it. The snap must run AFTER the height above
      // lands: snapping in the resize event itself measured the pre-keyboard
      // panel, and the log ended exactly one keyboard-height short.
      if (document.activeElement === inputEl) scrollDown(true);
      else scrollDown(); // stick-aware: pinned readers stay pinned through the resize
    }
    // batch repositions into animation frames — per-event style writes made
    // the panel visibly chase (jitter) while iOS panned the visual viewport
    var rafId = null;
    function schedule() {
      if (rafId) return;
      rafId = requestAnimationFrame(function () { rafId = null; apply(); });
    }
    vv.addEventListener('resize', schedule);
    vv.addEventListener('scroll', schedule);
    panel.__rkApplyViewport = apply;

    // Phones: a swipe the chat can't consume must die INSIDE the panel —
    // otherwise iOS chains it to the page, pans the visual viewport (the
    // body is position-fixed), and the pinned panel fights the pan: jitter
    // + the site peeking out below the chat. Two guards:
    if (window.matchMedia('(max-width: 767px)').matches) {
      // 1) never rest exactly on the scroller's edge — an edge-start swipe
      //    rubber-bands and chains even when the content overflows
      scrollEl.addEventListener('touchstart', function () {
        if (scrollEl.scrollTop <= 0) scrollEl.scrollTop = 1;
        var max = scrollEl.scrollHeight - scrollEl.clientHeight;
        if (max > 1 && scrollEl.scrollTop >= max) scrollEl.scrollTop = max - 1;
      }, { passive: true });
      // 2) swallow any pan that no scrollable element under the finger can
      //    take (the empty chat: intro + chips, nothing to scroll)
      panel.addEventListener('touchmove', function (e) {
        var el = e.target;
        while (el && el !== panel) {
          if (el.scrollHeight > el.clientHeight + 1 || el.scrollWidth > el.clientWidth + 1) {
            var st = getComputedStyle(el);
            if (/(auto|scroll)/.test(st.overflowY + st.overflowX)) return; // real scroller — let it work
          }
          el = el.parentElement;
        }
        e.preventDefault();
      }, { passive: false });
    }
  }

  // B1: assistant chip beside every search bar. With a stored conversation it
  // reads "Tęsti pokalbį"; without one it reads "Konsultantė" (S.widget) and
  // opens the fresh panel with the starter suggestions (2026-07-12) — the
  // entry point exists before the first chat too. Label is (re)set here on
  // every call so it flips the moment a conversation appears or expires.
  var continueChips = [];
  function updateContinueChips() {
    var hasChat = !!state.history.length;
    var show = !state.opened;
    var unread = false;
    try {
      unread = hasChat && state.history[state.history.length - 1].role === 'assistant' &&
        Number(localStorage.getItem('rk:seen') || 0) < state.history.length;
    } catch (e) {}
    var wantLabel = hasChat ? (S['continue'] || '') : (S.widget || S.title || '');
    continueChips.forEach(function (chip) {
      chip.hidden = !show;
      chip.classList.toggle('rk-continue--unread', !!unread);
      var lab = chip.querySelector('.rk-continue__label');
      // write only on real change: this runs from the MutationObserver, and
      // an unconditional textContent write IS a childList mutation — the
      // observer re-fires on it and the page spins in a 100% CPU loop
      if (lab && lab.textContent !== wantLabel) lab.textContent = wantLabel;
    });
  }

  function positionPanel() {
    var headerBottom = getComputedStyle(document.documentElement).getPropertyValue('--header-bottom-position');
    if (headerBottom && headerBottom.trim()) panel.style.setProperty('--rk-top', headerBottom.trim());
    else {
      var header = document.querySelector('header');
      panel.style.setProperty('--rk-top', (header ? Math.max(0, header.getBoundingClientRect().bottom) : 64) + 'px');
    }
  }

  function renderStarterVisibility() {
    var starter = panel.querySelector('.rk-starter');
    starter.style.display = state.history.length || state.sending || (quizState && quizState.active) ? 'none' : '';
  }

  // Stick-to-bottom follow (2026-07-14, replaced the old no-auto-follow):
  // while the customer sits at (or near) the bottom, streamed text and card
  // blocks push the view along like any modern chat; scrolling UP to read
  // disengages following — no yanking mid-read — and returning to the
  // bottom re-engages it. Explicit moments (open/restore, sending, quiz
  // steps) force-snap and re-engage. Writes are synchronous: an rAF queue
  // here both stalls in throttled tabs and races the async scroll event
  // (content grows between the snap and the event, the handler reads a big
  // gap and kills the stick — that bug shipped for ~20 minutes).
  // (settleScrollUntil covers the moment right after opening: restored card
  // blocks load async and grow the log.)
  var settleScrollUntil = 0;
  var stickBottom = true;

  function scrollDown(force) {
    if (!scrollEl) return;
    if (force) stickBottom = true;
    if (stickBottom) scrollEl.scrollTop = scrollEl.scrollHeight;
  }

  function settleScroll() {
    if (Date.now() < settleScrollUntil) scrollDown(true);
    else scrollDown();
  }

  function addMsg(role, text) {
    var el = document.createElement('div');
    el.className = 'rk-msg rk-msg--' + role;
    if (text) el.textContent = text;
    msgsEl.appendChild(el);
    renderStarterVisibility();
    scrollDown();
    return el;
  }

  // Capture the product a turn originated on. Product chips show this anchor
  // to the customer (multi-product sessions otherwise become a stack of
  // identical "Ar tiks mano odai?" bubbles); typed turns retain it only as
  // hidden history metadata. Thumbnail from the PDP's og:image; the strip is
  // context, not a recommendation — no price, no button.
  function pagePctx() {
    var t = cfg.page_context && cfg.page_context.title;
    if (!t) return null;
    var img = '';
    var m = document.querySelector('meta[property="og:image"]');
    if (m && m.content) {
      img = m.content;
      // Shopify CDN serves via cdn.shopify.com OR the shop domain (/cdn/shop/)
      if (img.indexOf('cdn.shopify.com') !== -1 || img.indexOf('/cdn/shop/') !== -1) {
        img += (img.indexOf('?') === -1 ? '?' : '&') + 'width=96';
      }
    }
    return {
      t: String(t).slice(0, 90),
      h: String((cfg.page_context && cfg.page_context.handle) || '').slice(0, 255),
      img: img,
    };
  }

  function attachPctx(el, pctx) {
    if (!pctx || !pctx.t) return;
    var strip = document.createElement('div');
    strip.className = 'rk-pctx';
    if (pctx.img) {
      var im = document.createElement('img');
      im.src = pctx.img;
      im.alt = '';
      im.loading = 'lazy';
      strip.appendChild(im);
    }
    var sp = document.createElement('span');
    sp.textContent = pctx.t;
    strip.appendChild(sp);
    el.insertBefore(strip, el.firstChild);
  }

  function addStatus(text) {
    var el = document.createElement('div');
    el.className = 'rk-status';
    el.textContent = text;
    msgsEl.appendChild(el);
    scrollDown();
    return el;
  }

  // ---------------- native cards (§7 Strategy A) ----------------
  function fetchCards(container, handles, layout, label, steps) {
    var block = document.createElement('div');
    block.className = 'rk-block rk-block--' + layout;
    if (label) {
      // labelled rows = routine periods (RYTE/VAKARE): on desktop they render
      // as a step LIST, one product per line — the 3-col grid wrapped a
      // 5-step period into ragged 3+2 rows
      block.className += ' rk-routine-list';
      var lab = document.createElement('div');
      lab.className = 'rk-block__label';
      lab.textContent = label;
      block.appendChild(lab);
    }
    var mount = document.createElement('div');
    mount.className = 'rk-block__cards';
    if (layout === 'grid') {
      // reserved space (2026-07-14): N card-shaped shimmer placeholders in
      // the SAME grid the real cards use — the block claims its final size
      // immediately, so the load is a content swap, not a layout snap
      // no-empty: FoxTheme globally hides EVERY empty div
      // (div:empty:not(..., .no-empty) { display: none }) — without the
      // escape class the whole placeholder skeleton collapses to nothing
      var ph = '<div class="rk-cards rk-cards--n' + handles.length + '">';
      for (var pi = 0; pi < handles.length; pi++) {
        // routine step captions ride the placeholder too — the step names
        // are known before the fetch, so they show during loading
        var step = steps && steps[pi] ? '<div class="rk-card__step">' + esc(steps[pi]) + '</div>' : '';
        ph += '<div class="rk-card rk-card--ph"><div class="rk-card__media no-empty">' + step + '</div><div class="rk-card__info">' +
          '<div class="rk-ph-bar no-empty"></div><div class="rk-ph-bar rk-ph-bar--short no-empty"></div><div class="rk-ph-bar rk-ph-bar--price no-empty"></div><div class="rk-ph-btn no-empty"></div>' +
          '</div></div>';
      }
      mount.innerHTML = ph + '</div>';
    } else {
      // compare table: generic shimmer. no-empty is load-bearing — FoxTheme
      // hides all empty divs, which kept EVERY skeleton invisible since v0
      // (the loading state was display:none and card loads snapped in cold)
      mount.classList.add('rk-skel', 'no-empty');
    }
    block.appendChild(mount);
    container.appendChild(block);
    scrollDown();
    // the unfold animation grows the block ~300ms with NO scroll events —
    // nothing re-pins mid-growth, so the anchored text drifted down and then
    // snapped back on the next delta. Keep the reader glued through it
    // (scrollDown is stick-aware: scrolled-up readers are left alone).
    var pinT0 = performance.now();
    (function pinLoop() {
      scrollDown();
      if (performance.now() - pinT0 < 360) requestAnimationFrame(pinLoop);
    })();

    fetch(API + '/cards?handles=' + encodeURIComponent(handles.join(',')) + '&layout=' + layout)
      .then(function (r) { if (!r.ok) throw new Error('cards ' + r.status); return r.text(); })
      .then(function (html) {
        mount.classList.remove('rk-skel');
        mount.innerHTML = html;
        mount.classList.add('rk-cards-in'); // content fades in over the reserved space
        // routine step captions: overlay on each card's image (index-aligned
        // with the handle list — /cards renders handles in order)
        if (steps && steps.length) {
          mount.querySelectorAll('.rk-card').forEach(function (card, ci) {
            if (!steps[ci]) return;
            var media = card.querySelector('.rk-card__media');
            if (!media || media.querySelector('.rk-card__step')) return;
            var badge = document.createElement('div');
            badge.className = 'rk-card__step';
            badge.textContent = steps[ci];
            media.appendChild(badge);
          });
        }
        document.dispatchEvent(new CustomEvent('konsultante:cards-loaded', { detail: { handles: handles } }));
        attributeCart('assist');
        settleScroll();
      })
      .catch(function () {
        mount.classList.remove('rk-skel');
        // graceful fallback: plain links to the products
        mount.innerHTML = handles.map(function (h) {
          return '<a class="rk-fallback-link" href="' + esc(ROOT + 'products/' + h) + '">' + esc(h.replace(/-/g, ' ')) + '</a>';
        }).join('');
      });
  }

  // Conversion attribution. Cards link to the product page, so most assisted
  // purchases used to go uncounted: only a chat-card add-to-cart stamped the
  // cart. Now the session is stamped as soon as cards are SHOWN ("assist",
  // once per page load — the attribute rides the cart token from then on)
  // and again, as "atc", when a card's own button adds to the cart. The
  // orders webhook reads both and records which one it was.
  function attributeCart(kind) {
    if (!state.session) return;
    if (kind !== 'atc') {
      if (state.attributed) return;
      state.attributed = true;
    }
    var attrs = { _konsultante: state.session };
    if (kind === 'atc') attrs._konsultante_atc = state.session;
    fetch(ROOT.replace(/\/$/, '') + '/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: attrs }),
    }).catch(function () {});
  }

  // Open the theme's cart drawer with fresh contents the way the theme's own
  // product forms do: publish cartUpdate — CartItems re-renders ONLY the
  // drawer body/footer, so the drawer chrome (close button + its listeners)
  // survives — then show(). A cart:refresh innerHTML swap would orphan the
  // close button's click listener (they bind once, at connect time).
  function openCartDrawer(cart, added) {
    try {
      var drawer = document.querySelector('cart-drawer');
      var FT = window.FoxTheme;
      if (drawer && FT && FT.pubsub && FT.pubsub.PUB_SUB_EVENTS && added && added.sections) {
        cart.sections = added.sections;
        FT.pubsub.publish(FT.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cart });
        if (typeof drawer.show === 'function') drawer.show();
        return;
      }
      // last resort (theme internals unavailable): full drawer re-render + open
      document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: true, source: 'konsultante' } }));
    } catch (e) { /* the add succeeded; the drawer is a nicety */ }
  }

  // AJAX add-to-cart for the chat cards: success state on the button, header
  // cart bubble refresh, cart drawer opens with the item, conversion
  // attribution, atc beacon. Falls back to the product page if the add fails
  // (e.g. just went out of stock).
  function addToCart(form) {
    var btn = form.querySelector('button[type="submit"]');
    if (!btn || btn.disabled) return;
    var label = btn.textContent;
    btn.disabled = true;
    btn.textContent = '…';

    // ask the theme which sections to bundle (the cart drawer registers
    // itself) so the add response carries their re-rendered HTML
    var sectionsToBundle = [];
    try {
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
      );
    } catch (e) { /* theme internals unavailable */ }
    var fd = new FormData(form);
    if (sectionsToBundle.length) {
      fd.append('sections', sectionsToBundle.join(','));
      fd.append('sections_url', window.location.pathname);
    }

    fetch(ROOT.replace(/\/$/, '') + '/cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: fd,
    })
      .then(function (r) { if (!r.ok) throw new Error('add ' + r.status); return r.json(); })
      .then(function (added) {
        btn.textContent = '✓';
        btn.classList.add('rk-card__atc--added');
        setTimeout(function () {
          btn.textContent = label;
          btn.classList.remove('rk-card__atc--added');
          btn.disabled = false;
        }, 2500);
        beacon('atc', { from: 'card' });
        attributeCart('atc');
        fetch(ROOT.replace(/\/$/, '') + '/cart.js')
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            // refresh any visible cart count bubbles
            document.querySelectorAll('.cart-count-bubble, [data-cart-count]').forEach(function (el) {
              el.textContent = cart.item_count;
            });
            openCartDrawer(cart, added);
          })
          .catch(function () {});
      })
      .catch(function () {
        btn.disabled = false;
        btn.textContent = label;
        var url = form.getAttribute('data-product-url');
        if (url) window.location.href = url;
      });
  }

  // ---------------- CTA renderers ----------------
  function renderCta(container, attrs) {
    if (attrs.type === 'quiz') {
      // lt/lv/et run the quiz INSIDE the chat (2026-07-14) — same questions
      // and scoring as the page quiz, no navigation, profile written locally
      if (QUIZ_I18N[cfg.locale]) {
        var qb = document.createElement('button');
        qb.type = 'button';
        qb.className = 'rk-cta btn btn--primary';
        qb.innerHTML = '<span class="btn__text">' + esc(S.quiz) + '</span>';
        qb.addEventListener('click', startQuiz);
        container.appendChild(qb);
        return;
      }
      // en/de: the chat quiz copy exists only in lt/lv/et — keep linking to
      // the per-market quiz page (empty entry = no button on that market;
      // legacy single-field fallback keeps LT working against an older server)
      var map = (state.remote && state.remote.quiz_urls) || null;
      var quizUrl;
      if (map && Object.prototype.hasOwnProperty.call(map, cfg.market)) {
        quizUrl = map[cfg.market] || '';
      } else {
        quizUrl = cfg.market === 'lt'
          ? ((state.remote && state.remote.quiz_url) || '/pages/odos-testas')
          : '';
      }
      if (!quizUrl) return;
      var a = document.createElement('a');
      a.className = 'rk-cta btn btn--primary';
      a.href = ROOT.replace(/\/$/, '') + quizUrl;
      a.innerHTML = '<span class="btn__text">' + esc(S.quiz) + '</span>';
      container.appendChild(a);
      return;
    }
    if (attrs.type === 'checkout') {
      var c = document.createElement('a');
      c.className = 'rk-cta btn btn--primary';
      c.href = ROOT.replace(/\/$/, '') + '/checkout';
      c.innerHTML = '<span class="btn__text">' + esc(S.cta_checkout || '→ Checkout') + '</span>';
      c.addEventListener('click', function () { beacon('cart_checkout_click', {}); });
      container.appendChild(c);
      return;
    }
    if (attrs.type === 'bis' && attrs.handle) renderBis(container, attrs.handle);
    if (attrs.type === 'contact') renderContact(container);
  }

  // In-chat contact form (2026-07-21): the assistant offers it when the
  // question needs a human (discount codes, order changes). Button first —
  // the form expands on demand and submits to our server, LINKED to this
  // conversation, so the admin answers with the whole transcript in view.
  function renderContact(container) {
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'rk-cta btn btn--primary no-empty';
    btn.innerHTML = '<span class="btn__text">✉️ ' + esc(S.ct_btn || 'Palikti žinutę') + '</span>';
    container.appendChild(btn);
    btn.addEventListener('click', function () {
      if (container.querySelector('.rk-contact')) return;
      var f = document.createElement('div');
      f.className = 'rk-contact no-empty';
      f.innerHTML =
        '<input type="text" class="rk-ct-name" maxlength="80" placeholder="' + esc(S.ct_name || 'Vardas') + '">' +
        '<input type="email" class="rk-ct-email" maxlength="120" placeholder="' + esc(S.ct_email || 'El. paštas') + '">' +
        '<input type="text" class="rk-ct-order" maxlength="40" placeholder="' + esc(S.ct_order || 'Užsakymo nr. (neprivaloma)') + '">' +
        '<textarea class="rk-ct-msg" maxlength="2000" rows="3" placeholder="' + esc(S.ct_msg || 'Jūsų žinutė') + '"></textarea>' +
        '<button type="button" class="rk-ct-send btn btn--primary no-empty"><span class="btn__text">' + esc(S.ct_send || 'Siųsti') + '</span></button>' +
        '<div class="rk-ct-note no-empty" hidden></div>';
      container.appendChild(f);
      settleScroll();
      var note = f.querySelector('.rk-ct-note');
      f.querySelector('.rk-ct-send').addEventListener('click', function () {
        var name = f.querySelector('.rk-ct-name').value.trim();
        var email = f.querySelector('.rk-ct-email').value.trim();
        var msg = f.querySelector('.rk-ct-msg').value.trim();
        if (!name || !msg || !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
          note.hidden = false; note.className = 'rk-ct-note bad no-empty';
          note.textContent = S.ct_err || 'Nepavyko išsiųsti. Bandykite dar kartą.';
          return;
        }
        this.disabled = true;
        fetch(API + '/contact', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            session_id: state.session,
            name: name,
            email: email,
            order_ref: f.querySelector('.rk-ct-order').value.trim(),
            message: msg,
          }),
        }).then(function (r) {
          if (!r.ok) throw 0;
          f.innerHTML = '<div class="rk-ct-note ok no-empty">' + esc(S.ct_ok || 'Ačiū! Gavome jūsų žinutę.') + '</div>';
          btn.remove();
          settleScroll();
        }).catch(function () {
          note.hidden = false; note.className = 'rk-ct-note bad no-empty';
          note.textContent = S.ct_err || 'Nepavyko išsiųsti. Bandykite dar kartą.';
          f.querySelector('.rk-ct-send').disabled = false;
        });
      });
    });
  }

  function renderBis(container, handle) {
    var wrap = document.createElement('div');
    wrap.className = 'rk-bis';
    wrap.innerHTML =
      '<input type="email" class="rk-bis__input" placeholder="' + esc(S.bis_email) + '">' +
      '<button type="button" class="rk-bis__btn btn btn--primary"><span class="btn__text">' + esc(S.bis_btn) + '</span></button>' +
      '<div class="rk-bis__msg" hidden></div>';
    container.appendChild(wrap);
    var input = wrap.querySelector('.rk-bis__input');
    var btn = wrap.querySelector('.rk-bis__btn');
    var msg = wrap.querySelector('.rk-bis__msg');
    btn.addEventListener('click', function () {
      var email = (input.value || '').trim();
      if (!/.+@.+\..+/.test(email)) { input.focus(); return; }
      btn.disabled = true;
      fetch(ROOT.replace(/\/$/, '') + '/products/' + encodeURIComponent(handle) + '.js')
        .then(function (r) { return r.json(); })
        .then(function (p) {
          var v = (p.variants || []).find(function (x) { return !x.available; }) || (p.variants || [])[0];
          if (!v) throw new Error('no variant');
          var company = (state.remote && state.remote.klaviyo_company_id) || '';
          return fetch('https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=' + encodeURIComponent(company), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
            body: JSON.stringify({
              data: {
                type: 'back-in-stock-subscription',
                attributes: { channels: ['EMAIL'], profile: { data: { type: 'profile', attributes: { email: email } } } },
                relationships: { variant: { data: { type: 'catalog-variant', id: '$shopify:::$default:::' + v.id } } },
              },
            }),
          });
        })
        .then(function (res) {
          if (!(res.ok || res.status === 202)) throw new Error('bis ' + res.status);
          wrap.innerHTML = '<div class="rk-bis__msg rk-bis__msg--ok">' + esc(S.bis_ok) + '</div>';
        })
        .catch(function () {
          btn.disabled = false;
          msg.hidden = false;
          msg.className = 'rk-bis__msg rk-bis__msg--err';
          msg.textContent = S.bis_err;
        });
    });
  }

  // ---------------- turnstile ----------------
  var turnstileWidget = null;
  function ensureTurnstile() {
    if (!state.remote || !state.remote.turnstile_sitekey) return;
    turnstileWrap.hidden = false;
    if (turnstileWidget !== null) return;
    turnstileWidget = 'loading';
    var mount = turnstileWrap.querySelector('.rk-turnstile-mount');
    function render() {
      turnstileWidget = window.turnstile.render(mount, {
        sitekey: state.remote.turnstile_sitekey,
        callback: function (token) { state.turnstileToken = token; },
        'expired-callback': function () { state.turnstileToken = null; },
      });
    }
    if (window.turnstile) return render();
    var s = document.createElement('script');
    s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    s.async = true;
    s.onload = render;
    document.head.appendChild(s);
  }

  // ---------------- chat transport ----------------
  function submitInputText(text) {
    var pending = state.pendingSend;
    state.pendingSend = null;
    if (pending && pending.text === text) {
      sendMessage(text, pending.route, pending.meta);
    } else {
      sendMessage(text, 'panel');
    }
  }

  function recentlyViewedIds() {
    try {
      var items = JSON.parse(localStorage.getItem('hypertheme:recently-viewed') || '[]');
      return Array.isArray(items) ? items.slice(0, 3) : [];
    } catch (e) { return []; }
  }

  function serializeTag(tag, value) {
    if (tag === 'routine') {
      var ser = function (hs, ls) {
        return (hs || []).map(function (h, i) {
          var l = (ls || [])[i];
          return l ? l + ':' + h : h;
        }).join(',');
      };
      return '<routine>am: ' + ser(value.am, value.amLabels) + ' | pm: ' + ser(value.pm, value.pmLabels) + '</routine>';
    }
    if (tag === 'cta') {
      return '<cta type="' + (value.type || '') + '"' + (value.handle ? ' handle="' + value.handle + '"' : '') + '>';
    }
    if (tag === 'search') return '<search>' + value + '</search>';
    return '<' + tag + '>' + (Array.isArray(value) ? value.join(',') : value) + '</' + tag + '>';
  }

  function sendMessage(text, route, meta) {
    if (state.sending) return;
    // Cart-origin conversations re-attach a FRESH cart snapshot on every
    // turn: the cart note rides the user message server-side only, so a
    // follow-up ("ir divi toneri — kuru labāk?") otherwise reaches the model
    // without the cart it is being asked about. Fresh fetch also tracks
    // items added/removed between turns.
    if (state.cartMode && !state.cartPayload) {
      state.sending = true; // hold the double-tap lock across the fetch
      refreshCartPayload().then(function () {
        state.sending = false;
        sendMessageNow(text, route, meta);
      });
      return;
    }
    sendMessageNow(text, route, meta);
  }

  function sendMessageNow(text, route, meta) {
    if (state.sending) return;
    openPanel(route);
    if (state.turnstileNeeded && !state.turnstileToken) {
      state.pendingSend = { text: text, route: route, meta: meta || null };
      ensureTurnstile();
      inputEl.value = text;
      return;
    }
    state.pendingSend = null;
    state.sending = true;
    sendBtn.disabled = true;
    quizAbandon(); // typing over a running quiz abandons it — nothing written
    deactivateReplyChips(); // any outgoing message answers or supersedes the open question
    inputEl.value = '';
    inputEl.style.height = 'auto';

    // history first: addMsg re-evaluates the starter (intro + chips), which
    // must vanish the instant the first message exists — not at first token
    var isFirstMessage = !state.history.length;
    // Preserve the originating PDP on every PDP turn so typed fit questions,
    // their one-tap refinements and exact repeats keep the right target after
    // navigation. Only product chips render the visible anchor; typed/general
    // turns carry the metadata invisibly.
    var pctx = meta && meta.pctx ? meta.pctx : pagePctx();
    var showPctx = route === 'chip' || route === 'chip_oos' || route === 'chip_oos_product'
      || (route === 'quiz_next' && meta && meta.pctx);
    if (pctx && !showPctx) pctx.img = ''; // hidden provenance needs no thumbnail payload
    var userEntry = { role: 'user', content: text, route: route };
    if (pctx) {
      userEntry.pctx = pctx;
      if (!showPctx) userEntry.pctx_hidden = true;
    }
    if (meta && meta.chip_id) userEntry.chip_id = String(meta.chip_id).slice(0, 100);
    state.history.push(userEntry);
    saveHistory(); // the question is stored NOW — a mid-answer exit used to lose the whole turn
    attachPctx(addMsg('user', text), showPctx ? pctx : null);
    scrollDown(true); // sending is an explicit action — snap down

    // the assistant bubble is created lazily on the first streamed content —
    // an empty bubble sitting next to the "thinking…" status looked broken
    var aEl = null;
    var textNode = null;
    // trailing whitespace of the streamed text is held back one delta: a
    // tag frame deletes it, and painting it first cost 1-2 blank line boxes
    // (22-45px) of shrink right at the reading line (2026-07-25)
    var pendingTail = '';
    var thumbsEl = null;
    function ensureBubble() {
      if (!aEl) {
        aEl = addMsg('assistant', '');
        textNode = document.createElement('span');
        aEl.appendChild(textNode);
        // thumbs ride along hidden from the first token — revealing them at
        // completion is a zero-shift visibility flip, not a late append that
        // nudges the pinned view (the last of the answer-jump family)
        thumbsEl = document.createElement('div');
        thumbsEl.className = 'rk-thumbs';
        thumbsEl.style.visibility = 'hidden';
        thumbsEl.innerHTML = '<button type="button" data-rk-thumb="up" aria-label="👍">👍</button><button type="button" data-rk-thumb="down" aria-label="👎">👎</button>';
        aEl.appendChild(thumbsEl);
      }
      return aEl;
    }
    // Routes we KNOW run long start with a specific activity + duration hint
    // instead of the generic "galvoja" (sold-out flow legitimately searches
    // 20–45s; customers re-tapped thinking it was dead).
    var initialStatus = S.thinking;
    if ((route === 'chip_oos' || route === 'chip_oos_product') && S.wait_oos) initialStatus = S.wait_oos;
    else if (route === 'cart' && S.wait_cart) initialStatus = S.wait_cart;
    var status = addStatus(initialStatus);
    var genericStatus = function () {
      return status.textContent === S.thinking || status.textContent === S.searching;
    };
    // staged reassurance: a frozen status line reads as "broken". slow1 only
    // upgrades GENERIC text (never replaces a specific route message with a
    // vaguer one); slow2 ("taking longer than usual") applies to everything.
    var slowTimers = [
      setTimeout(function () { if (status.isConnected && S.slow1 && genericStatus()) status.textContent = S.slow1; }, 12000),
      setTimeout(function () { if (status.isConnected && S.slow2) status.textContent = S.slow2; }, 30000),
    ];

    var rawAssistant = '';
    var gotAnything = false;
    // A dead socket used to lock the chat until the server's 150 s deadline
    // (state.sending swallowed every new send, with no cancel affordance).
    // Abort when no frame arrives for 60 s: the server heartbeats every 15 s,
    // so a live stream never trips this — a dropped proxy connection does.
    var ctrl = typeof AbortController === 'function' ? new AbortController() : null;
    var lastFrameAt = Date.now();
    var watchdog = setInterval(function () {
      if (ctrl && Date.now() - lastFrameAt > 60000) ctrl.abort();
    }, 5000);
    // the partial answer, stored by the pagehide handler if the customer
    // leaves mid-stream (tapping a card is the common exit)
    state.inflight = { raw: '', gotAnything: false, saved: false };

    fetch(API + '/chat', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      signal: ctrl ? ctrl.signal : undefined,
      body: JSON.stringify({
        session_id: state.session,
        market: cfg.market,
        locale: cfg.locale,
        page_context: cfg.page_context,
        recently_viewed: recentlyViewedIds(),
        route: isFirstMessage && state.openedVia ? state.openedVia : route,
        turnstile_token: state.turnstileToken,
        profile: state.profile || loadProfile(),
        cart: takeCartPayload(),
        messages: state.history.slice(-20),
      }),
    })
      .then(function (res) {
        if (res.status === 403) return res.json().then(function (j) { throw { code: 'turnstile', j: j }; });
        if (res.status === 429) return res.json().then(function (j) { throw { code: 'limit', j: j }; });
        if (!res.ok || !res.body) throw { code: 'http' };
        var reader = res.body.getReader();
        var decoder = new TextDecoder();
        var buffer = '';
        function pump() {
          return reader.read().then(function (r) {
            lastFrameAt = Date.now();
            if (r.done) return;
            buffer += decoder.decode(r.value, { stream: true });
            var parsed = RK.parseSSE(buffer);
            buffer = parsed[1];
            parsed[0].forEach(handleFrame);
            return pump();
          });
        }
        function handleFrame(f) {
          if (f.event === 'delta') {
            gotAnything = true;
            status.remove();
            ensureBubble();
            rawAssistant += f.data.text;
            state.inflight.raw = rawAssistant; state.inflight.gotAnything = true;
            // a fresh span (start of answer / right after a card block) drops
            // its leading newlines — pre-wrap would render them as selectable
            // blank lines; spacing around blocks belongs to CSS margins
            var chunk = pendingTail + f.data.text;
            pendingTail = /\s*$/.exec(chunk)[0];
            chunk = chunk.slice(0, chunk.length - pendingTail.length);
            if (chunk) {
              textNode.textContent += textNode.textContent ? chunk : chunk.replace(/^\s+/, '');
            }
            scrollDown();
          } else if (f.event === 'tag' && f.data.tag === 'profile') {
            // invisible memory tag: store it, keep it out of the bubble AND
            // out of stored history (the model must not see its own tag back)
            saveProfile(f.data.value);
          } else if (f.event === 'tag') {
            gotAnything = true;
            status.remove();
            ensureBubble();
            rawAssistant += ' ' + serializeTag(f.data.tag, f.data.value) + ' ';
            state.inflight.raw = rawAssistant; state.inflight.gotAnything = true;
            // the text before the block already ends clean: its trailing
            // whitespace was never painted (pendingTail), so drop it silently
            pendingTail = '';
            handleTag(aEl, f.data.tag, f.data.value);
            textNode = document.createElement('span');
            aEl.appendChild(textNode);
            if (thumbsEl) aEl.appendChild(thumbsEl); // keep the reserved row last — a move, not a resize
          } else if (f.event === 'tool') {
            if (!status.isConnected) {
              // mid-answer search: re-appear as "searching" (any earlier wait
              // text is stale by now). It goes INSIDE the bubble, directly
              // above the span being written — the same "grow above the pinned
              // reading line" doctrine the card blocks use. Appended to msgsEl
              // it sat BELOW the answer and cost 2 × ~32px at the reading line:
              // +32 on appear, −32 on the next delta's status.remove().
              status.textContent = S.searching;
              var statusHost = ensureBubble();
              if (textNode && textNode.parentNode === statusHost) statusHost.insertBefore(status, textNode);
              else statusHost.appendChild(status);
              scrollDown();
            } else if (status.textContent === S.thinking) {
              // searching beats "thinking", but never downgrades a specific
              // route message ("Ieškau alternatyvų sandėlyje — gali užtrukti…")
              status.textContent = S.searching;
            }
          } else if (f.event === 'done') {
            state.turnstileNeeded = !!f.data.turnstile_next;
            if (state.turnstileNeeded) {
              state.turnstileToken = null;
              ensureTurnstile();
              // Cloudflare tokens are single-use and the gate applies to EVERY
              // message from the Nth on: without a reset the solved widget never
              // fires its callback again and the next send waits forever
              if (window.turnstile && typeof turnstileWidget === 'string' && turnstileWidget !== 'loading') {
                try { window.turnstile.reset(turnstileWidget); } catch (e) { /* widget gone */ }
              }
            }
          } else if (f.event === 'error') {
            throw { code: 'model' };
          }
        }
        return pump();
      })
      .then(function () {
        finishAssistant(aEl, rawAssistant, gotAnything);
      })
      .catch(function (err) {
        status.remove();
        if (err && err.code === 'turnstile') {
          state.turnstileNeeded = true;
          state.turnstileToken = null;
          state.pendingSend = { text: text, route: route, meta: meta || null };
          ensureTurnstile();
          if (aEl) aEl.remove();
          state.history.pop();
          addStatus(S.verify);
          inputEl.value = text;
        } else if (err && err.code === 'limit') {
          ensureBubble().textContent = S.limit + ((state.remote && state.remote.contact_email) || 'info@rossi.lt');
          state.history.pop();
        } else {
          if (!gotAnything) { ensureBubble().textContent = S.error; state.history.pop(); }
          else finishAssistant(aEl, rawAssistant, gotAnything);
        }
      })
      .finally(function () {
        slowTimers.forEach(clearTimeout);
        clearInterval(watchdog);
        state.inflight = null;
        state.sending = false;
        sendBtn.disabled = false;
        saveHistory();
        scrollDown();
      });
  }

  // Leaving mid-answer (tapping a card → PDP is the common exit) used to lose
  // the whole turn from the stored conversation: history was saved only when
  // the stream ended. Store what has arrived so "Tęsti pokalbį" shows it.
  window.addEventListener('pagehide', function () {
    var f = state.inflight;
    if (!f || !f.gotAnything || f.saved) return;
    f.saved = true;
    state.history.push({ role: 'assistant', content: f.raw.trim() });
    saveHistory();
  });

  function finishAssistant(aEl, rawAssistant, gotAnything) {
    if (!gotAnything || !aEl) return;
    var entry = { role: 'assistant', content: rawAssistant.trim() };
    var last = state.history[state.history.length - 1];
    // pagehide already stored a partial copy of this very answer (bfcache
    // restore let the stream finish): replace it instead of duplicating
    if (state.inflight && state.inflight.saved && last && last.role === 'assistant') state.history[state.history.length - 1] = entry;
    else state.history.push(entry);
    saveHistory();
    // the customer watched this answer arrive — it is not unread
    if (state.opened) { try { localStorage.setItem('rk:seen', String(state.history.length)); } catch (e) {} }
    // the row has occupied its space (hidden) since the first token —
    // revealing it must not move anything
    var thumbs = aEl.querySelector('.rk-thumbs');
    if (thumbs) { thumbs.style.visibility = ''; return; }
    thumbs = document.createElement('div');
    thumbs.className = 'rk-thumbs';
    thumbs.innerHTML = '<button type="button" data-rk-thumb="up" aria-label="👍">👍</button><button type="button" data-rk-thumb="down" aria-label="👎">👎</button>';
    aEl.appendChild(thumbs);
  }

  // Skin profile memory: the model emits an invisible <profile> tag when the
  // customer states their own skin type/concerns; we keep it in localStorage
  // and send it with every future conversation so the assistant never has to
  // re-ask. Nothing personal beyond "mišri oda" ever leaves the browser store.
  function loadProfile() {
    try {
      var p = JSON.parse(localStorage.getItem('rk:profile'));
      return p && (p.skin || p.concerns) ? p : null;
    } catch (e) { return null; }
  }
  function saveProfile(attrs) {
    if (!attrs || (!attrs.skin && !attrs.concerns)) return;
    try {
      var cur = loadProfile() || {};
      if (attrs.skin) cur.skin = String(attrs.skin).slice(0, 40);
      if (attrs.concerns) cur.concerns = String(attrs.concerns).slice(0, 120);
      cur.ts = Date.now();
      localStorage.setItem('rk:profile', JSON.stringify(cur));
      state.profile = cur;
    } catch (e) { /* storage unavailable */ }
  }

  // Card blocks render at the TOP of the answer bubble, text below (2026-07-14):
  // cards fetch async and land ~a second after their tag streams — inserted
  // in stream order (after the text) their late growth shoved the text the
  // customer was reading out of view. Above the text, the growth happens
  // above the bottom-pinned reading line, which therefore never moves. The
  // stream/stored order is untouched — this is presentation only, and
  // multiple blocks (routine AM/PM) keep their relative order inside the head.
  function blockHead(container) {
    var head = container.querySelector(':scope > .rk-block-head');
    if (!head) {
      head = document.createElement('div');
      head.className = 'rk-block-head';
      container.insertBefore(head, container.firstChild);
    }
    return head;
  }

  function handleTag(container, tag, value) {
    if (tag === 'profile') return saveProfile(value); // invisible — render nothing
    if (tag === 'products') return fetchCards(blockHead(container), value, 'grid');
    if (tag === 'compare') return fetchCards(blockHead(container), value, 'compare');
    if (tag === 'routine') {
      var head = blockHead(container);
      if (value.am && value.am.length) fetchCards(head, value.am, 'grid', S.am, value.amLabels);
      if (value.pm && value.pm.length) fetchCards(head, value.pm, 'grid', S.pm, value.pmLabels);
      return;
    }
    if (tag === 'search') {
      beacon('search_escape', { q: value });
      closePanel();
      window.location.href = ROOT.replace(/\/$/, '') + '/search?q=' + encodeURIComponent(value) + '&options%5Bprefix%5D=last';
      return;
    }
    if (tag === 'cta') return renderCta(container, value);
    if (tag === 'chips') return renderReplyChips(container, value);
  }

  // One-tap answer buttons for the assistant's own question (<chips> tag).
  // Only the newest row stays active — a new question or ANY sent message
  // (tap or typed) retires older rows so a stale question can't be answered.
  function deactivateReplyChips() {
    msgsEl.querySelectorAll('.rk-reply-chips:not(.rk-reply-chips--done)').forEach(function (row) {
      row.classList.add('rk-reply-chips--done');
      row.querySelectorAll('button').forEach(function (b) { b.disabled = true; });
    });
  }

  function renderReplyChips(container, labels) {
    if (!labels || !labels.length) return;
    deactivateReplyChips();
    var row = document.createElement('div');
    row.className = 'rk-reply-chips';
    labels.forEach(function (label) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'rk-chip';
      b.textContent = label;
      b.addEventListener('click', function () {
        beacon('reply_chip', { label: label });
        sendMessage(label, 'reply_chip');
      });
      row.appendChild(b);
    });
    container.appendChild(row);
    scrollDown();
  }

  // ---------------- in-chat skin quiz (2026-07-14) ----------------
  // Deterministic port of the page quiz (v2): the chat asks the 5 fixed
  // questions with tap-chips and scores locally via RK.quizEvaluate — zero
  // model turns, zero tokens, byte-identical answer keys. Completion writes
  // rk:profile (same contract as the page quiz) and stores ONE assistant
  // history entry so the result survives navigation and the model sees it
  // next turn. Abandoning (close / typing over it) writes nothing.
  var QUIZ_STEPS = [
    { key: 'q1', opts: ['q1_oily', 'q1_mixed', 'q1_dry', 'q1_normal'] },
    { key: 'q2', opts: ['q2_dry_or_normal', 'q2_mixed', 'q2_oily'] },
    { key: 'q3', opts: ['q3_dry', 'q3_normal', 'q3_none'] },
    { key: 'q4', opts: ['concern_acne', 'concern_pigment', 'concern_redness', 'concern_aging', 'concern_dull', 'concern_dehydration'], multi: true },
    { key: 'q5', opts: ['sens_yes', 'sens_some', 'sens_no'] },
  ];
  var QUIZ_I18N = {
    lt: {
      q: {
        q1: 'Kaip Jūsų oda atrodo praėjus kelioms valandoms po prausimosi?',
        q2: 'Kaip atrodo Jūsų veido poros?',
        q3: 'Ar Jūsų oda pleiskanoja, jaučiasi šiurkšti ar tempiama?',
        q4: 'Kokie odos rūpesčiai Jums šiuo metu aktualiausi? (pasirinkite iki 2)',
        q5: 'Ar Jūsų oda jautri — linkusi parausti, peršėti ar reaguoti į naujas priemones?',
      },
      o: {
        q1_oily: 'Blizga visa veido oda', q1_mixed: 'Blizga tik T zona (kakta, nosis, smakras)', q1_dry: 'Matinė, jaučiamas tempimas ar sausumas', q1_normal: 'Subalansuota — be blizgesio ir be tempimo',
        q2_dry_or_normal: 'Vos pastebimos, neišsiplėtusios', q2_mixed: 'Išsiplėtusios daugiausia T zonoje', q2_oily: 'Pastebimai išsiplėtusios didelėje veido dalyje',
        q3_dry: 'Taip, dažnai — tai kelia diskomfortą', q3_normal: 'Retai — pojūtis praeina panaudojus drėkinamąsias priemones', q3_none: 'Ne, tokių požymių nebūna',
        concern_acne: 'Spuogai, inkštirai, bėrimai', concern_pigment: 'Pigmentinės dėmės, netolygus odos tonas', concern_redness: 'Paraudimai, sudirgimas', concern_aging: 'Raukšlelės, stangrumo praradimas', concern_dull: 'Pavargusi, blankios spalvos oda', concern_dehydration: 'Dehidratacijos, sausumo pojūtis',
        sens_yes: 'Taip, dažnai', sens_some: 'Kartais', sens_no: 'Ne, toleruoja daugumą priemonių',
      },
      hero: 'Jūsų oda — {t}',
      next: 'Toliau',
      label: 'Odos testas',
      nextRoutine: 'Sudarykite man rutiną',
      nextCatLabel: 'Rekomenduoti produktą:',
      cats: [
        ['Serumas', 'Rekomenduokite serumą mano odos tipui'],
        ['Kremas', 'Rekomenduokite veido kremą mano odos tipui'],
        ['Prausiklis', 'Rekomenduokite prausiklį mano odos tipui'],
        ['Tonikas', 'Rekomenduokite toniką mano odos tipui'],
        ['Apsauga nuo saulės', 'Rekomenduokite apsaugą nuo saulės mano odos tipui'],
      ],
      names: { sausa: 'sausa', misri: 'mišri', riebi: 'riebi', normali: 'normali' },
      traits: {
        sausa: 'Jaučiamas tempimas · Matinė oda · Vos matomos poros',
        misri: 'Blizga T zonoje · Sausesni skruostai · Matomos poros',
        riebi: 'Blizga visa oda · Išsiplėtusios poros · Greitai riebaluojasi',
        normali: 'Subalansuota · Komfortiška visą dieną · Vos matomos poros',
      },
      sum: {
        sausa: 'Rutina sudaryta drėkinti ir atkurti odos barjerą.',
        misri: 'Rutina subalansuota T zonai ir sausesnėms sritims — drėkina, bet neriebaluoja.',
        riebi: 'Rutina padeda subalansuoti riebalų gamybą ir išvalyti poras neperdžiovinant odos.',
        normali: 'Rutina palaiko natūralų odos balansą ir apsaugo nuo išorės poveikio.',
      },
    },
    lv: {
      q: {
        q1: 'Kā Jūsu āda izskatās dažas stundas pēc sejas mazgāšanas?',
        q2: 'Kā izskatās Jūsu sejas poras?',
        q3: 'Vai Jūsu āda lobās, ir raupja vai jūtams savilkums?',
        q4: 'Kādas ādas problēmas Jums šobrīd ir aktuālākās? (izvēlieties līdz 2)',
        q5: 'Vai Jūsu āda ir jutīga — mēdz apsārt, sūrstēt vai reaģēt uz jauniem produktiem?',
      },
      o: {
        q1_oily: 'Spīd visa sejas āda', q1_mixed: 'Spīd tikai T zona (piere, deguns, zods)', q1_dry: 'Matēta, jūtams savilkums vai sausums', q1_normal: 'Līdzsvarota — bez spīduma un savilkuma sajūtas',
        q2_dry_or_normal: 'Tikko pamanāmas, nepaplašinātas', q2_mixed: 'Paplašinātas galvenokārt T zonā', q2_oily: 'Izteikti paplašinātas lielā sejas daļā',
        q3_dry: 'Jā, bieži — tas rada diskomfortu', q3_normal: 'Reti — sajūta pāriet pēc mitrinošu līdzekļu lietošanas', q3_none: 'Nē, šādu pazīmju nav',
        concern_acne: 'Pūtītes, melnie punktiņi, izsitumi', concern_pigment: 'Pigmentācijas plankumi, nevienmērīgs ādas tonis', concern_redness: 'Apsārtums, kairinājums', concern_aging: 'Grumbiņas, tvirtuma zudums', concern_dull: 'Nogurusi, blāva āda', concern_dehydration: 'Sausuma, dehidratācijas sajūta',
        sens_yes: 'Jā, bieži', sens_some: 'Dažreiz', sens_no: 'Nē, panes lielāko daļu produktu',
      },
      hero: 'Jūsu āda — {t}',
      next: 'Tālāk',
      label: 'Ādas tests',
      nextRoutine: 'Izveidojiet man rutīnu',
      nextCatLabel: 'Ieteikt produktu:',
      cats: [
        ['Serums', 'Iesakiet serumu manam ādas tipam'],
        ['Krēms', 'Iesakiet sejas krēmu manam ādas tipam'],
        ['Attīrītājs', 'Iesakiet attīrīšanas līdzekli manam ādas tipam'],
        ['Toniks', 'Iesakiet toniku manam ādas tipam'],
        ['Saules aizsardzība', 'Iesakiet saules aizsardzību manam ādas tipam'],
      ],
      names: { sausa: 'sausa', misri: 'kombinēta', riebi: 'taukaina', normali: 'normāla' },
      traits: {
        sausa: 'Jūtams savilkums · Matēta āda · Tikko pamanāmas poras',
        misri: 'Spīd T zona · Sausāki vaigi · Pamanāmas poras',
        riebi: 'Spīd visa āda · Paplašinātas poras · Ātri kļūst taukaina',
        normali: 'Līdzsvarota · Komfortabla visu dienu · Tikko pamanāmas poras',
      },
      sum: {
        sausa: 'Rutīna izveidota, lai mitrinātu un atjaunotu ādas barjeru.',
        misri: 'Rutīna līdzsvaro T zonu un sausākās zonas — mitrina, bet nepadara ādu taukainu.',
        riebi: 'Rutīna palīdz līdzsvarot sebuma veidošanos un attīrīt poras, neizsausinot ādu.',
        normali: 'Rutīna uztur ādas dabisko līdzsvaru un aizsargā no ārējās vides.',
      },
    },
    et: {
      q: {
        q1: 'Kuidas näeb sinu nahk välja paar tundi pärast näopesu?',
        q2: 'Kuidas näevad välja sinu poorid?',
        q3: 'Kas sinu nahk ketendab, tundub kare või pingul?',
        q4: 'Millised nahamured on sinu jaoks praegu kõige olulisemad? (vali kuni 2)',
        q5: 'Kas sinu nahk on tundlik — kipub punetama, kipitama või reageerima uutele toodetele?',
      },
      o: {
        q1_oily: 'Läigib kogu nägu', q1_mixed: 'Läigib ainult T-tsoon (laup, nina, lõug)', q1_dry: 'Matt, nahk tundub pingul või kuiv', q1_normal: 'Tasakaalus — ei läigi ega tundu pingul',
        q2_dry_or_normal: 'Vaevu märgatavad, mitte laienenud', q2_mixed: 'Laienenud peamiselt T-tsoonis', q2_oily: 'Märgatavalt laienenud suurel osal näost',
        q3_dry: 'Jah, sageli — see tekitab ebamugavust', q3_normal: 'Harva — tunne kaob pärast niisutavate toodete kasutamist', q3_none: 'Ei, selliseid märke pole',
        concern_acne: 'Vistrikud, mustpead, lööve', concern_pigment: 'Pigmendilaigud, ebaühtlane nahatoon', concern_redness: 'Punetus, ärritus', concern_aging: 'Kortsukesed, elastsuse kadu', concern_dull: 'Väsinud, tuhm nahk', concern_dehydration: 'Kuivuse ja veevaeguse tunne',
        sens_yes: 'Jah, sageli', sens_some: 'Mõnikord', sens_no: 'Ei, talub enamikku tooteid',
      },
      hero: 'Sinu nahk — {t}',
      next: 'Edasi',
      label: 'Nahatest',
      nextRoutine: 'Koosta mulle rutiin',
      nextCatLabel: 'Soovita toodet:',
      cats: [
        ['Seerum', 'Soovita seerumit minu nahatüübile'],
        ['Kreem', 'Soovita näokreemi minu nahatüübile'],
        ['Puhastaja', 'Soovita puhastajat minu nahatüübile'],
        ['Toonik', 'Soovita toonikut minu nahatüübile'],
        ['Päikesekaitse', 'Soovita päikesekaitset minu nahatüübile'],
      ],
      names: { sausa: 'kuiv', misri: 'kombineeritud', riebi: 'rasune', normali: 'normaalne' },
      traits: {
        sausa: 'Pingultunne · Matt nahk · Vaevu märgatavad poorid',
        misri: 'T-tsoon läigib · Kuivemad põsed · Märgatavad poorid',
        riebi: 'Kogu nahk läigib · Laienenud poorid · Muutub kiiresti rasuseks',
        normali: 'Tasakaalus · Mugav terve päeva · Vaevu märgatavad poorid',
      },
      sum: {
        sausa: 'Rutiin on koostatud naha niisutamiseks ja kaitsebarjääri taastamiseks.',
        misri: 'Rutiin tasakaalustab T-tsooni ja kuivemaid piirkondi — niisutab, kuid ei muuda nahka rasuseks.',
        riebi: 'Rutiin aitab tasakaalustada rasueritust ja puhastada poore nahka kuivatamata.',
        normali: 'Rutiin hoiab naha loomulikku tasakaalu ja kaitseb väliskeskkonna mõjude eest.',
      },
    },
    fi: {
      q: {
        q1: 'Miltä ihosi näyttää muutama tunti kasvojen pesun jälkeen?',
        q2: 'Miltä huokosesi näyttävät?',
        q3: 'Hilseileekö ihosi, tuntuuko se karhealta tai kiristävältä?',
        q4: 'Mitkä ihohuolet ovat sinulle nyt tärkeimpiä? (valitse enintään 2)',
        q5: 'Onko ihosi herkkä — punoittaako se, kirveleekö tai reagoiko uusiin tuotteisiin?',
      },
      o: {
        q1_oily: 'Koko kasvojen iho kiiltää', q1_mixed: 'Vain T-alue kiiltää (otsa, nenä, leuka)', q1_dry: 'Matta, iho tuntuu kireältä tai kuivalta', q1_normal: 'Tasapainossa — ei kiiltoa eikä kiristystä',
        q2_dry_or_normal: 'Hädin tuskin näkyvät, eivät laajentuneet', q2_mixed: 'Laajentuneet lähinnä T-alueella', q2_oily: 'Selvästi laajentuneet suurella osalla kasvoja',
        q3_dry: 'Kyllä, usein — se tuntuu epämukavalta', q3_normal: 'Harvoin — tunne häviää kosteuttavien tuotteiden jälkeen', q3_none: 'Ei, tällaisia merkkejä ei ole',
        concern_acne: 'Finnit, mustapäät ja muut epäpuhtaudet', concern_pigment: 'Pigmenttimuutokset, epätasainen ihonsävy', concern_redness: 'Punoitus, ärsytys', concern_aging: 'Juonteet, kimmoisuuden heikkeneminen', concern_dull: 'Väsyneen näköinen, samea iho', concern_dehydration: 'Kuivuuden ja kosteusvajeen tunne',
        sens_yes: 'Kyllä, usein', sens_some: 'Joskus', sens_no: 'Ei, sietää useimmat tuotteet',
      },
      hero: 'Ihosi — {t}',
      next: 'Seuraava',
      label: 'Ihotesti',
      nextRoutine: 'Kokoa minulle rutiini',
      nextCatLabel: 'Suosittele tuotetta:',
      cats: [
        ['Seerumi', 'Suosittele seerumia ihotyypilleni'],
        ['Voide', 'Suosittele kasvovoidetta ihotyypilleni'],
        ['Puhdistusaine', 'Suosittele puhdistusainetta ihotyypilleni'],
        ['Kasvovesi', 'Suosittele kasvovettä ihotyypilleni'],
        ['Aurinkosuoja', 'Suosittele aurinkosuojaa ihotyypilleni'],
      ],
      names: { sausa: 'kuiva iho', misri: 'sekaiho', riebi: 'rasvoittuva iho', normali: 'normaali iho' },
      traits: {
        sausa: 'Kiristävä tunne · Matta iho · Hädin tuskin näkyvät huokoset',
        misri: 'T-alue kiiltää · Kuivemmat posket · Näkyvät huokoset',
        riebi: 'Koko iho kiiltää · Laajentuneet huokoset · Rasvoittuu nopeasti',
        normali: 'Tasapainossa · Mukava koko päivän · Hädin tuskin näkyvät huokoset',
      },
      sum: {
        sausa: 'Rutiini on koottu kosteuttamaan ja vahvistamaan ihon suojakerrosta.',
        misri: 'Rutiini tasapainottaa T-aluetta ja kuivempia alueita — kosteuttaa muuttamatta ihoa rasvaiseksi.',
        riebi: 'Rutiini auttaa tasapainottamaan talintuotantoa ja puhdistamaan huokosia kuivattamatta ihoa.',
        normali: 'Rutiini ylläpitää ihon luonnollista tasapainoa ja suojaa ulkoisilta rasituksilta.',
      },
    },
  };
  // profile contract (brief §6): canonical skin values are Lithuanian,
  // accented, on ALL markets; concerns tokenize to short LT words
  var QUIZ_CANON = { misri: 'mišri', sausa: 'sausa', riebi: 'riebi', normali: 'normali' };
  var QUIZ_CONCERN_LT = {
    concern_acne: 'spuogai', concern_pigment: 'pigmentacija', concern_redness: 'raudonis',
    concern_aging: 'raukšlelės', concern_dull: 'blankumas', concern_dehydration: 'dehidratacija',
  };

  var quizState = null;

  // pressed-state via inline importance: SOME cross-origin sheet restyles
  // disabled buttons and beat even our !important class rule — the chosen
  // chip rendered white. Inline style with priority is the end of that war.
  function quizMarkSel(b, on) {
    b.classList.toggle('rk-chip--sel', on);
    if (on) {
      b.style.setProperty('background', 'var(--rk-navy)', 'important');
      b.style.setProperty('border-color', 'var(--rk-navy)', 'important');
      b.style.setProperty('color', '#fff', 'important');
    } else {
      b.style.removeProperty('background');
      b.style.removeProperty('border-color');
      b.style.removeProperty('color');
    }
  }

  function startQuiz() {
    if (quizState && quizState.active) { scrollDown(); return; }
    if (state.sending) return; // a quiz finishing under a failing stream got popped as the user message
    var lang = QUIZ_I18N[cfg.locale] ? cfg.locale : 'lt'; // entry points gate to lt/lv/et; unknown → lt
    openPanel('quiz');
    quizState = { active: true, lang: lang, step: 0, answers: [], picked: [] };
    beacon('quiz_start', {});
    renderStarterVisibility();
    quizAsk();
  }

  function quizAbandon() {
    if (!quizState || !quizState.active) return;
    quizState = null;
    deactivateReplyChips(); // retire the open question row — nothing is written
    beacon('quiz_abandon', {});
    renderStarterVisibility();
  }

  function quizAsk() {
    var L = QUIZ_I18N[quizState.lang];
    var step = QUIZ_STEPS[quizState.step];
    // quiz steps read as a FORM, not chat: centered progress label ("Odos
    // testas · 2/5") + centered question, chips centered below — the mode
    // switch away from left-aligned bubbles is what tells the customer a
    // quiz is running
    var lab = document.createElement('div');
    lab.className = 'rk-quiz-label';
    lab.textContent = L.label + ' · ' + (quizState.step + 1) + '/' + QUIZ_STEPS.length;
    msgsEl.appendChild(lab);
    var qEl = document.createElement('div');
    qEl.className = 'rk-quiz-q';
    qEl.textContent = L.q[step.key];
    msgsEl.appendChild(qEl);
    var row = document.createElement('div');
    row.className = 'rk-reply-chips rk-quiz-row';
    quizState.picked = [];
    var nextBtn = null;
    step.opts.forEach(function (k) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'rk-chip';
      b.textContent = L.o[k];
      b.addEventListener('click', function () {
        if (!quizState || !quizState.active) return;
        if (!step.multi) { quizMarkSel(b, true); return quizAnswer(row, [k]); }
        var i = quizState.picked.indexOf(k);
        if (i >= 0) { quizState.picked.splice(i, 1); quizMarkSel(b, false); }
        else if (quizState.picked.length < 2) { quizState.picked.push(k); quizMarkSel(b, true); }
        // a third pick is rejected silently (max 2) — deselect one to swap
        nextBtn.disabled = !quizState.picked.length;
      });
      row.appendChild(b);
    });
    if (step.multi) {
      nextBtn = document.createElement('button');
      nextBtn.type = 'button';
      nextBtn.className = 'rk-chip rk-chip--next';
      nextBtn.textContent = L.next;
      nextBtn.disabled = true;
      nextBtn.addEventListener('click', function () {
        if (quizState && quizState.active && quizState.picked.length) quizAnswer(row, quizState.picked.slice(0));
      });
      row.appendChild(nextBtn);
    }
    msgsEl.appendChild(row);
    scrollDown();
  }

  function quizAnswer(row, keys) {
    // no user-bubble echo: the pressed chip (kept highlighted while its row
    // greys out) IS the visible record of the answer
    row.classList.add('rk-reply-chips--done');
    row.querySelectorAll('button').forEach(function (b) { b.disabled = true; });
    quizState.answers = quizState.answers.concat(keys);
    quizState.step += 1;
    if (quizState.step < QUIZ_STEPS.length) return quizAsk();
    quizFinish();
  }

  function quizFinish() {
    var L = QUIZ_I18N[quizState.lang];
    var answers = quizState.answers;
    var type = RK.quizEvaluate(answers);
    var hero = L.hero.replace('{t}', L.names[type]);

    var el = addMsg('assistant', '');
    var h = document.createElement('div');
    h.className = 'rk-quiz-hero';
    h.textContent = hero;
    var tr = document.createElement('div');
    tr.className = 'rk-quiz-traits';
    tr.textContent = L.traits[type];
    var su = document.createElement('div');
    su.textContent = L.sum[type];
    el.appendChild(h);
    el.appendChild(tr);
    el.appendChild(su);

    var skin = QUIZ_CANON[type];
    if (skin) {
      if (answers.indexOf('sens_yes') >= 0) skin += ',jautri';
      var concerns = answers.map(function (k) { return QUIZ_CONCERN_LT[k]; }).filter(Boolean).join(',');
      saveProfile({ skin: skin, concerns: concerns }); // merges + caps, never throws
    }

    // one history entry: the transcript survives navigation and the model
    // reads the result as conversation context on the next real turn
    state.history.push({ role: 'assistant', content: hero + '\n' + L.traits[type] + '\n' + L.sum[type] });
    saveHistory();
    try { localStorage.setItem('rk:seen', String(state.history.length)); } catch (e) {}

    quizState = null;
    beacon('quiz_done', {});
    quizNextSteps(L);
    scrollDown();
  }

  // The result must not dead-end: one-tap next steps. If the quiz interrupted
  // a real question (the model offers it mid-conversation when the skin type
  // is unknown), the FIRST chip re-asks that question — now answerable with
  // the fresh profile. PDP provenance now survives on every user turn, so a
  // product-fit question can safely resume too. Then the routine builder and
  // category asks. Ephemeral DOM like the quiz itself.
  function quizNextSteps(L) {
    function chipBtn(row, label, msg, meta) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'rk-chip';
      b.textContent = label;
      b.addEventListener('click', function () {
        beacon('quiz_next', { label: label.slice(0, 60) });
        sendMessage(msg, 'quiz_next', meta);
      });
      row.appendChild(b);
    }
    var row = document.createElement('div');
    row.className = 'rk-reply-chips rk-quiz-row';
    var lastQ = null;
    for (var i = state.history.length - 1; i >= 0; i--) {
      var m = state.history[i];
      // Skip the short button answer that merely led to the quiz; resume the
      // actual request before it. Older history has no route and behaves as
      // before.
      if (m.role === 'user' && m.route !== 'reply_chip') { lastQ = m; break; }
    }
    if (lastQ) {
      var qText = lastQ.content || '';
      var lab = qText.length > 32 ? qText.slice(0, 32).replace(/\s+\S*$/, '') + '…' : qText;
      chipBtn(row, '↻ ' + lab, qText, {
        chip_id: lastQ.chip_id || '',
        pctx: lastQ.pctx || null,
      });
    }
    chipBtn(row, '✨ ' + L.nextRoutine, L.nextRoutine);
    msgsEl.appendChild(row);
    var catLab = document.createElement('div');
    catLab.className = 'rk-quiz-label rk-quiz-next-label';
    catLab.textContent = L.nextCatLabel;
    msgsEl.appendChild(catLab);
    var cats = document.createElement('div');
    cats.className = 'rk-reply-chips rk-quiz-row';
    L.cats.forEach(function (c) { chipBtn(cats, c[0], c[1]); });
    msgsEl.appendChild(cats);
  }

  // ---------------- search-bar integrations ----------------

  function eachSearchInput(fn) {
    document.querySelectorAll('predictive-search input[type="search"]').forEach(fn);
  }

  function startPlaceholderRotation() {
    var phs = S.placeholders || [];
    if (!phs.length) return;
    var i = 0;
    setInterval(function () {
      i = (i + 1) % (phs.length + 1); // slot 0 keeps the original placeholder
      eachSearchInput(function (input) {
        if (document.activeElement === input || input.value) return;
        if (!input.dataset.rkPh) input.dataset.rkPh = input.getAttribute('placeholder') || '';
        input.classList.add('rk-ph-swap');
        setTimeout(function () {
          input.setAttribute('placeholder', i === 0 ? input.dataset.rkPh : phs[i - 1]);
          input.classList.remove('rk-ph-swap');
        }, 180);
      });
    }, 3500);
  }

  function loadIndexOnce() {
    if (state.index || state.indexLoading) return;
    state.indexLoading = true;
    fetch(API + '/local-index.json')
      .then(function (r) { return r.json(); })
      .then(function (idx) { state.index = idx; })
      .catch(function () {})
      .finally(function () { state.indexLoading = false; });
  }

  function wireSearchBars() {
    document.querySelectorAll('predictive-search').forEach(function (ps) {
      if (ps.__rkWired) return;
      ps.__rkWired = true;
      var input = ps.querySelector('input[type="search"]');
      var form = ps.querySelector('form');
      if (!input || !form) return;

      input.addEventListener('focus', loadIndexOnce, { once: true });

      var field = ps.querySelector('.search__field') || form;

      // No extra assistant pills on the bar — the search input itself is the
      // entry point (typed questions route to the chat on submit, below; the
      // no-results state offers the consultant CTA). The one chip that stays
      // is continue-chat while a session lives.

      // B1: assistant chip — sits in the SAME row, right of the field (the
      // form becomes a wrapping flex row so the sticky header never grows a
      // second bar). Continue-chat when a session lives, "Konsultantė" fresh
      // entry otherwise; updateContinueChips owns label + visibility.
      // The HEADER instances pre-render the chip in Liquid so the final
      // layout is the first paint (no load-time bar expansion) — adopt that
      // node; create one only where the snippet did not (search page).
      form.classList.add('rk-cont-row'); // no-op when server-rendered
      var cont = ps.querySelector('.rk-continue');
      if (!cont) {
        cont = document.createElement('button');
        cont.type = 'button';
        cont.className = 'rk-continue';
        cont.hidden = true;
        cont.innerHTML = '<span class="rk-continue__bloom" aria-hidden="true">🌸 </span><span class="rk-continue__label">' + esc(S['continue'] || '') + '</span><span class="rk-continue__dot" aria-hidden="true"></span>';
        field.insertAdjacentElement('afterend', cont);
      }
      cont.addEventListener('click', function () {
        if (state.history.length) {
          beacon('continued');
          openPanel('continue');
        } else {
          openPanel('header'); // empty history → starter suggestions render
        }
      });
      continueChips.push(cont);

      // lane 2: clear questions submit into the assistant instead of /search
      form.addEventListener('submit', function (e) {
        var qv = input.value.trim();
        if (!qv) return;
        var lane = RK.detectLane(qv, state.index);
        if (lane === 'question') {
          e.preventDefault();
          e.stopPropagation();
          sendMessage(qv, 'submit');
        } else {
          beacon('search_used', { lane: lane, q: qv.slice(0, 120) });
        }
      }, true);

      // ask-mode: while the typed query parses as a question (the lane that
      // will submit into the assistant), the bar itself signals it — blush
      // ring on the field, ✦ glyph pulsing. No extra buttons anywhere.
      input.addEventListener('input', function () {
        var qv = input.value.trim();
        field.classList.toggle('rk-ask-mode', !!qv && RK.detectLane(qv, state.index) === 'question');
      });
      form.addEventListener('reset', function () {
        setTimeout(function () { field.classList.remove('rk-ask-mode'); }, 0);
      });

      // no-results rescue beacon (the empty state renders [data-rk-rescue])
      var results = ps.querySelector('[id^="PredictiveSearchResults"]');
      if (results && 'MutationObserver' in window) {
        var lastShown = '';
        new MutationObserver(function () {
          var cta = results.querySelector('[data-rk-rescue]');
          if (cta && input.value.trim() && input.value.trim() !== lastShown) {
            lastShown = input.value.trim();
            cta.setAttribute('data-rk-query', lastShown);
            beacon('rescue_shown', { q: lastShown });
          }
        }).observe(results, { childList: true, subtree: true });
      }
    });
  }

  // navigation entry points: any [data-rk-open] element or link to
  // #konsultante (mobile sticky-bar block, menu items) opens the chat
  function wireAssistantLinks() {
    document.addEventListener('click', function (e) {
      var a = e.target.closest && e.target.closest('[data-rk-open], a[href*="#konsultante"]');
      if (!a) return;
      e.preventDefault();
      // if the tap came from inside the menu drawer, close it underneath
      try {
        var drawer = document.getElementById('MenuDrawer');
        if (drawer && typeof drawer.hide === 'function') drawer.hide();
      } catch (err) { /* theme internals shifted — cosmetic */ }
      openPanel('nav');
    });
  }

  // no-results CTA -> hand the query to the assistant, whatever lane it was
  function wireRescueClicks() {
    document.addEventListener('click', function (e) {
      var cta = e.target.closest && e.target.closest('[data-rk-rescue]');
      if (!cta) return;
      e.preventDefault();
      var qv = cta.getAttribute('data-rk-query') || '';
      beacon('rescue_used', { q: qv });
      if (qv) sendMessage(qv, 'rescue');
      else openPanel('rescue');
    });
    // /search page: rescue markup present on load
    var pageCta = document.querySelector('.template-search [data-rk-rescue]');
    if (pageCta) beacon('rescue_shown', { q: pageCta.getAttribute('data-rk-query') || '' });
  }

  // Collection-grid auto-heading cards ("Nežinote, kurį serumą rinktis?"):
  // the whole card is the tap target and tapping ASKS the mapped question —
  // the button says "Klausti", so pressing must ask, never open a blank chat.
  function wireCollectionCards() {
    document.addEventListener('click', function (e) {
      var card = e.target.closest && e.target.closest('[data-rk-ask]');
      if (!card) return;
      e.preventDefault();
      var q = card.getAttribute('data-rk-ask');
      if (q) sendMessage(q, 'collection_card');
    });
  }

  // Sold-out state of the CURRENT variant, read from the Liquid-rendered buy
  // button (the theme sets [disabled] on .product-form__submit — same contract
  // klaviyo-back-in-stock.liquid keys its :has() rule on). Re-queried every
  // call: the theme swaps the buy row on variant change, never hold references.
  // product-info first so a quick-view modal's form can't shadow the main PDP.
  function pdpSoldOut() {
    var btn = document.querySelector('product-info .product-form__submit') ||
      document.querySelector('.product-form__submit');
    if (btn) return !!btn.disabled;
    return null; // buy row mid-swap — caller keeps the current state
  }

  // Ensure pass for the OOS chip: idempotent, cheap, safe to run on every DOM
  // mutation (writes only on actual state change, so it never re-triggers the
  // observer loop).
  function syncOosChip() {
    var chip = document.querySelector('.rk-chip--oos');
    if (!chip) return;
    var soldOut = pdpSoldOut();
    if (soldOut === null) return;
    if (chip.hidden !== !soldOut) chip.hidden = !soldOut;
  }

  // ---------------- cart drawer entry ----------------
  // The pill is Liquid-rendered inside the drawer's addon strip with [hidden]
  // (zero layout shift, survives the drawer's server-rendered re-renders by
  // construction). We only unhide it when the remote flag says so, and re-apply
  // after every re-render via the body observer. Clicks are delegated —
  // re-renders would orphan direct listeners.
  var cartRowSeen = false;
  var emptyCardSeen = false;
  function syncCartBtn() {
    var enabled = !!(state.remote && state.remote.cart_assistant);
    var changed = false;
    // after the session's first cart question, the compat chip invites a
    // RE-check — the customer is usually editing and re-asking anyway.
    // The flag is keyed to the session id, so it self-expires with it.
    var recheck = '';
    try {
      if (S.cart_recheck && localStorage.getItem('rk:cart_asked') === state.session) recheck = S.cart_recheck;
    } catch (e) { /* storage unavailable */ }
    document.querySelectorAll('[data-rk-cart], [data-rk-cart-recs]').forEach(function (btn) {
      if (btn.hidden !== !enabled) { btn.hidden = !enabled; changed = true; }
      if (recheck && btn.hasAttribute('data-rk-cart')) {
        var lbl = btn.querySelector('.btn__text');
        // write only on real change — this runs from the MutationObserver,
        // and an unconditional write is itself a mutation (the chip-label
        // 100%-CPU loop taught us that)
        if (lbl && lbl.textContent.trim() !== recheck) lbl.textContent = recheck;
      }
      if (enabled && !cartRowSeen && 'IntersectionObserver' in window && !btn.__rkSeen) {
        btn.__rkSeen = true;
        new IntersectionObserver(function (entries, obs) {
          if (entries[0] && entries[0].isIntersecting && !cartRowSeen) {
            cartRowSeen = true;
            obs.disconnect();
            beacon('cart_row_view', {});
          }
        }, { threshold: 0.5 }).observe(btn);
      }
    });
    // empty-cart entry card: same kill switch; its own view beacon (the
    // volume baseline — tap rate means nothing without empty-drawer opens)
    document.querySelectorAll('[data-rk-empty]').forEach(function (btn) {
      if (btn.hidden !== !enabled) { btn.hidden = !enabled; changed = true; }
      if (enabled && !emptyCardSeen && 'IntersectionObserver' in window && !btn.__rkSeen) {
        btn.__rkSeen = true;
        new IntersectionObserver(function (entries, obs) {
          if (entries[0] && entries[0].isIntersecting && !emptyCardSeen) {
            emptyCardSeen = true;
            obs.disconnect();
            beacon('empty_cart_view', {});
          }
        }, { threshold: 0.5 }).observe(btn);
      }
    });
    // the pill is Liquid-visible so the drawer's swipe strip measures it at
    // init; if the kill switch just hid/revealed it, the strip's cached
    // bounds are stale — a resize event makes the component re-measure
    // (stale bounds were the "second scroll snaps back" bug)
    if (changed) {
      try { window.dispatchEvent(new Event('resize')); } catch (e) {}
    }
  }

  function takeCartPayload() {
    var p = state.cartPayload || null;
    state.cartPayload = null;
    return p;
  }

  // the chat panel and the theme drawer are separate overlays and the drawer
  // stacks above — close it so the conversation isn't buried underneath
  function closeCartDrawer() {
    try {
      var drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.hide === 'function') { drawer.hide(); return; }
      var closeBtn = drawer && drawer.querySelector('.drawer__close-btn');
      if (closeBtn) closeBtn.click();
    } catch (e) { /* cosmetic */ }
  }

  // kind: 'compat' (default — "ar dera tarpusavyje?") | 'recs' ("ko trūksta
  // rutinai?", route cart_recs — the 60-day evaluation separates the two)
  function buildCartPayload(cart) {
    if (!cart || !cart.item_count) return null;
    var freeShip = Math.round(Number(cfg.free_ship || 0) * 100);
    // Routine-gap advice needs the WHOLE cart. The old 20-line cap silently
    // hid more than half of large carts, so the model confidently called
    // steps "missing" when they could be present below the cutoff. A 100-line
    // cap stays well inside the 256kb request limit; explicit metadata makes
    // the rare larger cart degrade honestly instead of guessing.
    var itemLimit = 100;
    return {
      total: Math.round(cart.total_price) / 100,
      item_count: cart.item_count,
      line_count: cart.items.length,
      truncated: cart.items.length > itemLimit,
      free_ship_gap: freeShip && cart.total_price < freeShip
        ? Math.round(freeShip - cart.total_price) / 100 : 0,
      items: cart.items.slice(0, itemLimit).map(function (i) {
        return { t: i.product_title, h: i.handle || '', q: i.quantity, p: Math.round(i.final_line_price) / 100 };
      }),
    };
  }

  // never rejects — an empty/failed cart just means no cart note this turn
  function refreshCartPayload() {
    return fetch(ROOT.replace(/\/$/, '') + '/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) { state.cartPayload = buildCartPayload(cart); })
      .catch(function () { state.cartPayload = null; });
  }

  function openCartAssistant(kind) {
    fetch(ROOT.replace(/\/$/, '') + '/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        state.cartPayload = buildCartPayload(cart);
        if (!state.cartPayload) return;
        state.cartMode = true; // follow-ups keep re-attaching a fresh snapshot
        beacon('cart_row_click', { items: cart.item_count, kind: kind === 'recs' ? 'recs' : 'compat' });
        // the compat chip relabels to "Patikrinti pakeitimus" once this
        // session has asked a cart question — teaches that the assistant
        // notices cart edits (the server diffs snapshots between presses)
        try { localStorage.setItem('rk:cart_asked', state.session); } catch (e) { /* storage unavailable */ }
        // keep the drawer OPEN underneath — the chat covers it (rk-cart-origin
        // suppresses the drawer-over-chat lift), and closing the chat lands
        // the customer back on their cart (2026-07-12)
        document.documentElement.classList.add('rk-cart-origin');
        if (kind === 'recs') sendMessage(S.cart_recs_msg || S.cart_msg, 'cart_recs');
        else sendMessage(S.cart_msg, 'cart');
      })
      .catch(function () {});
  }

  function wireCartClicks() {
    document.addEventListener('click', function (e) {
      var t = e.target;
      if (!t || !t.closest) return;
      var ec = t.closest('[data-rk-empty]');
      if (ec) {
        e.preventDefault();
        beacon('empty_cart_click', {});
        document.documentElement.classList.add('rk-cart-origin'); // drawer stays open underneath
        openPanel('empty_cart'); // fresh chat, starter chips; first message carries the route
        return;
      }
      var recs = t.closest('[data-rk-cart-recs]');
      if (recs) { e.preventDefault(); openCartAssistant('recs'); return; }
      var btn = t.closest('[data-rk-cart]');
      if (btn) { e.preventDefault(); openCartAssistant(); return; }
      // sibling analytics: does the assistant cannibalize the calculator?
      var sib = t.closest('button.btn--small[aria-controls^="CartCoupon-"], button.btn--small[aria-controls^="ShippingCalculator-"]');
      if (sib) {
        beacon('cart_sibling_click', { which: /CartCoupon/.test(sib.getAttribute('aria-controls')) ? 'coupon' : 'shipping' });
      }
    });
  }

  function injectPdpChips() {
    if (!cfg.page_context || !cfg.page_context.handle) return;
    var anchor = document.querySelector('product-info .product-form__buttons') ||
      document.querySelector('.product-form__buttons');
    if (!anchor || document.querySelector('.rk-chips')) return;
    // category-aware chips resolved server-side per product
    // (/config?product=<handle> against the pdp-chips matrix); the static
    // per-locale set remains the fallback while the map is missing
    var chips = (state.remote && Array.isArray(state.remote.pdp_chips) && state.remote.pdp_chips.length)
      ? state.remote.pdp_chips
      : (S.pdp_chips || []).map(function (t) { return { id: null, text: t }; });
    var wrap = document.createElement('div');
    wrap.className = 'rk-chips';
    // Sold-out alternative chip — first position, solid blush, in the DOM from
    // birth with the truthful state (buy button if readable, else the Liquid
    // "available" of the selected variant — covers ?variant= deep links). Only
    // its hidden flag ever changes; syncOosChip keeps it honest on variant
    // switches.
    if (S.oos_chip && S.oos_msg) {
      var soldOutNow = pdpSoldOut();
      if (soldOutNow === null) soldOutNow = cfg.page_context.available === false;
      var oosBtn = document.createElement('button');
      oosBtn.type = 'button';
      oosBtn.className = 'rk-chip rk-chip--oos';
      oosBtn.hidden = !soldOutNow;
      oosBtn.textContent = '✦ ' + S.oos_chip;
      oosBtn.addEventListener('click', function () {
        beacon('chip_open', {
          handle: cfg.page_context.handle,
          chip: S.oos_chip,
          chip_id: 'pdp.oos',
          locale: cfg.locale,
          v: (state.remote && state.remote.pdp_chips_v) || null,
        });
        sendMessage(S.oos_msg, 'chip_oos_product');
      });
      wrap.appendChild(oosBtn);
    }
    // v3 mapping serves up to 4 chips ("3 mobile / 4 desktop"): the mobile row
    // is already a swipeable rail with ~3 chips visible, so all 4 render on
    // both breakpoints (a pre-v3 server still sends 3 — slice tolerates both)
    chips.slice(0, 4).forEach(function (chip) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'rk-chip';
      b.textContent = '🌸 ' + chip.text;
      b.addEventListener('click', function () {
        var chipId = chip.id ? 'pdp.static.' + chip.id : null;
        beacon('chip_open', {
          handle: cfg.page_context.handle,
          chip: chip.text,
          chip_id: chipId,
          locale: cfg.locale,
          v: (state.remote && state.remote.pdp_chips_v) || null,
        });
        sendMessage(chip.text, 'chip', { chip_id: chipId });
      });
      wrap.appendChild(b);
    });
    anchor.insertAdjacentElement('afterend', wrap);

    // phones: one-time gentle slide when the row scrolls into view — makes
    // the hidden 3rd chip obvious (only when the row actually overflows)
    if (window.matchMedia('(max-width: 767px)').matches) {
      // the right-edge fade lies once the user reaches the end of the row —
      // drop it there so the last chip never looks cut
      wrap.addEventListener('scroll', function () {
        var atEnd = wrap.scrollLeft + wrap.clientWidth >= wrap.scrollWidth - 6;
        wrap.classList.toggle('rk-chips--end', atEnd);
      }, { passive: true });

      var hinted = false;
      // a real touch beats the hint — never yank the row out of the user's finger
      wrap.addEventListener('touchstart', function () { hinted = true; }, { passive: true, once: true });
      var hint = function () {
        if (hinted) return;
        hinted = true;
        if (wrap.scrollWidth <= wrap.clientWidth + 8) return;
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return; // fade alone carries it
        setTimeout(function () {
          try {
            wrap.scrollTo({ left: 64, behavior: 'smooth' });
            setTimeout(function () { wrap.scrollTo({ left: 0, behavior: 'smooth' }); }, 650);
          } catch (e) { /* ancient browsers: fade alone carries the hint */ }
        }, 350);
      };
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries, obs) {
          if (entries[0] && entries[0].isIntersecting) { obs.disconnect(); hint(); }
        }, { threshold: 0.9 }).observe(wrap);
      } else {
        setTimeout(hint, 1200);
      }
    }
  }


  // ---------------- desktop launcher widget ----------------
  // Big screens have no always-visible entry point (mobile has the sticky-bar
  // block) — a floating "Konsultantė" pill at the bottom-left opens the chat.
  var launcherEl = null;

  function injectLauncher() {
    if (launcherEl) return;
    launcherEl = document.createElement('button');
    launcherEl.type = 'button';
    launcherEl.className = 'rk-launcher';
    launcherEl.innerHTML =
      '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">' +
      '<path d="M4 6.5A2.5 2.5 0 0 1 6.5 4h11A2.5 2.5 0 0 1 20 6.5v7.6a2.5 2.5 0 0 1-2.5 2.5H10l-4.2 3.9a.75.75 0 0 1-1.3-.55z" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/>' +
      '<path d="M12 6.6l1.15 2.75 2.75 1.15-2.75 1.15L12 14.4l-1.15-2.75-2.75-1.15 2.75-1.15z" fill="#FB828C"/>' +
      '</svg><span>' + esc(S.widget || S.title || '') + '</span>';
    launcherEl.addEventListener('click', function () { openPanel('widget'); });
    document.body.appendChild(launcherEl);
    updateLauncher();
  }

  function updateLauncher() {
    if (launcherEl) launcherEl.hidden = !!state.opened; // panel has its own close
  }

  // ---------------- nudge: proactive invitation (nudge-spec 2026-07-07) ----------------
  var nudgeEl = null;
  var nudgeTicker = null;

  function ssGetInt(key) {
    try { return parseInt(sessionStorage.getItem(key), 10) || 0; } catch (e) { return 0; }
  }
  function ssSet(key, val) {
    try { sessionStorage.setItem(key, String(val)); } catch (e) {}
  }
  function nudgeMemory() {
    try { return JSON.parse(localStorage.getItem('rk:nudge') || '{}') || {}; } catch (e) { return {}; }
  }
  function nudgeRemember(patch) {
    try {
      var cur = nudgeMemory();
      for (var k in patch) cur[k] = patch[k];
      localStorage.setItem('rk:nudge', JSON.stringify(cur));
    } catch (e) {}
  }
  // eligible templates only — never cart/checkout/account/home
  function nudgePageType() {
    var p = window.location.pathname;
    if (p.indexOf('/products/') === 0) return 'product';
    if (p.indexOf('/collections/') === 0) return 'collection';
    if (p.indexOf('/search') === 0) return 'search';
    return null;
  }

  function wireNudge() {
    var nc = state.remote && state.remote.nudge;
    if (!nc || !nc.text) return; // disabled / locale not enabled — server sends null
    var mem = nudgeMemory();
    if (mem.clicked) return; // they know the door exists — never again
    if (mem.dismissedUntil && Date.now() < mem.dismissedUntil) return;
    if (cfg.page_context && cfg.page_context.handle) ssSet('rk:pdp-views', ssGetInt('rk:pdp-views') + 1);
    if (!nudgePageType()) return; // active time accumulates on eligible pages only
    maybeNudge(); // the PDP-views threshold may already be crossed on load
    nudgeTicker = setInterval(function () {
      if (document.visibilityState !== 'visible') return; // GA4-style active time
      ssSet('rk:nudge:active-seconds', ssGetInt('rk:nudge:active-seconds') + 1);
      maybeNudge();
    }, 1000);
  }

  function maybeNudge() {
    if (nudgeEl) return; // max once per page load
    var nc = state.remote && state.remote.nudge;
    if (!nc) return;
    var mobile = window.matchMedia('(max-width: 767px)').matches;
    var trigger = RK.nudgeTrigger(mobile, ssGetInt('rk:nudge:active-seconds'), ssGetInt('rk:pdp-views'), nc);
    if (!trigger) return;
    // eligibility gates, checked at trigger time
    if (state.opened) return;
    // never nudge over an open cart drawer — its own assistant entries
    // (pills / empty-cart card) already own that moment
    if (document.querySelector('cart-drawer[open]')) return;
    try { if (sessionStorage.getItem('rk:opened')) return; } catch (e) {} // opened earlier this session
    if (state.history.length) return; // stored conversation → the continue chip owns them
    try { if (sessionStorage.getItem('rk:proactive-shown')) return; } catch (e) {} // one proactive prompt per session
    var mem = nudgeMemory();
    if (mem.clicked || (mem.dismissedUntil && Date.now() < mem.dismissedUntil)) return;
    if (nudgeTicker) { clearInterval(nudgeTicker); nudgeTicker = null; }
    showNudge(trigger, mobile);
  }

  function showNudge(trigger, mobile) {
    try { sessionStorage.setItem('rk:proactive-shown', '1'); } catch (e) {}
    nudgeRemember({ lastShown: Date.now() });
    var nc = state.remote.nudge;
    nudgeEl = document.createElement('div');
    nudgeEl.className = 'rk-nudge ' + (mobile ? 'rk-nudge--mobile' : 'rk-nudge--desktop');
    nudgeEl.innerHTML =
      '<button type="button" class="rk-nudge__body">🌸 ' + esc(nc.text) + '</button>' +
      '<button type="button" class="rk-nudge__close" aria-label="' + esc(S.close) + '">&times;</button>';
    document.body.appendChild(nudgeEl);
    requestAnimationFrame(function () { if (nudgeEl) nudgeEl.classList.add('rk-nudge--in'); });
    var payload = { device: mobile ? 'mobile' : 'desktop', trigger: trigger, page_type: nudgePageType(), locale: cfg.locale };
    beacon('nudge_shown', payload);
    nudgeEl.querySelector('.rk-nudge__body').addEventListener('click', function () {
      beacon('nudge_clicked', payload);
      nudgeRemember({ clicked: true });
      openPanel('nudge'); // openPanel hides the nudge itself
    });
    nudgeEl.querySelector('.rk-nudge__close').addEventListener('click', function () {
      beacon('nudge_dismissed', payload);
      nudgeRemember({ dismissedUntil: Date.now() + (nc.dismiss_suppress_days || 7) * 86400000 });
      hideNudge();
    });
  }

  function hideNudge() {
    if (!nudgeEl) return;
    var el = nudgeEl;
    el.classList.remove('rk-nudge--in');
    setTimeout(function () { el.remove(); }, 250);
    // nudgeEl stays truthy on purpose: never re-triggers this page load
  }

  // ---------------- boot ----------------
  function init() {
    state.session = session();
    loadHistory();

    var configUrl = API + '/config?locale=' + encodeURIComponent(cfg.locale || 'lt');
    if (cfg.page_context && cfg.page_context.handle) {
      configUrl += '&product=' + encodeURIComponent(cfg.page_context.handle);
    }
    fetch(configUrl)
      .then(function (r) { return r.json(); })
      .then(function (remote) {
        state.remote = remote;
        var bucket = RK.hashBucket(state.session);
        state.enabled = !!remote.enabled && bucket < Number(remote.traffic_pct || 0);
        if (!state.enabled) {
          document.documentElement.classList.add('rk-off');
          return;
        }
        startPlaceholderRotation();
        wireSearchBars();
        wireRescueClicks();
        wireAssistantLinks();
        wireCartClicks();
        wireCollectionCards();
        injectLauncher();
        injectPdpChips();
        syncCartBtn();
        updateContinueChips();
        wireNudge();
        // re-wire late-rendered search bars (drawers, sections loaded async);
        // the same pass keeps the OOS chip synced with variant switches and
        // re-injects the chips row if a section swap wiped it
        if ('MutationObserver' in window) {
          new MutationObserver(function () {
            wireSearchBars();
            updateContinueChips();
            injectPdpChips();
            syncOosChip();
            syncCartBtn(); // drawer re-renders reset the pill to hidden
          }).observe(document.body, { childList: true, subtree: true });
        }
      })
      .catch(function () {
        document.documentElement.classList.add('rk-off');
      });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
